import { useState, useEffect, useRef } from "react";
import {
  Home, UtensilsCrossed, SlidersHorizontal, User, Menu as MenuIcon,
  Sun, Moon, Printer, Bookmark, X, ChevronLeft, ChevronRight,
  Clock, Globe, BookOpen, MessageCircle, ArrowUp, Check, Lightbulb,
  Sparkles, Loader2, Brain, ThumbsUp, ThumbsDown, Calendar, Trash2,
  CornerUpLeft, Wallet, Gem, Target, FlaskConical, PenLine, ChefHat,
  Zap, Scale, Heart, Leaf, Mail, Users, ShoppingCart, BarChart3,
  Coins, CircleCheck, CircleX, CheckCheck, LogOut, RotateCcw, AlertTriangle, Star
} from "lucide-react";
import supabase from "./supabase";

const DAYS = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];

const MONTHS_SHORT = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
const DAY_NL = ["Zondag","Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag"];
const DAY_NL_SHORT = ["Zo","Ma","Di","Wo","Do","Vr","Za"];
function getNext7Days(){
  return Array.from({length:7},(_,i)=>{
    const d=new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate()+i);
    return {name:DAY_NL[d.getDay()],short:DAY_NL_SHORT[d.getDay()],num:d.getDate(),month:MONTHS_SHORT[d.getMonth()],isToday:i===0};
  });
}
const SK_PREFS = "wm_prefs_v4"; const SK_MEM = "wm_mem_v4"; const SK_WEEKS = "wm_weeks_v4"; const SK_ACC = "wm_acc_v4"; const SK_THEME = "wm_theme_v4"; const SK_USER = "wm_user_v1";
function load(k,fb){try{const v=localStorage.getItem(k);return v?JSON.parse(v):fb;}catch{return fb;}}
function save(k,v){try{localStorage.setItem(k,JSON.stringify(v));}catch{}}

function getWeekNr(){const n=new Date(),s=new Date(n.getFullYear(),0,1);return Math.ceil(((n-s)/86400000+s.getDay()+1)/7);}

const THEMES = {
  dark: {
    bg:"#0a0a0b", card:"#1c1c1e", cardAlt:"#2c2c2e", border:"#2c2c2e",
    text:"#f5f5f7", textSoft:"#98989d", textDim:"#48484a",
    accent:"#a78bfa", accentWarm:"#fb923c", accentRed:"#f87171", accentGreen:"#34d399",
    surface:"rgba(255,255,255,0.05)", navBg:"rgba(10,10,11,0.85)",
    shadow:"0 4px 24px rgba(0,0,0,0.5)", inputBg:"rgba(255,255,255,0.07)",
    chatUser:"rgba(167,139,250,0.15)", chatBot:"rgba(255,255,255,0.05)",
    gradient:"linear-gradient(135deg,#a78bfa,#fb923c)",
  },
  light: {
    bg:"#f2f2f7", card:"#ffffff", cardAlt:"#f2f2f7", border:"#e5e5ea",
    text:"#1c1c1e", textSoft:"#6e6e73", textDim:"#c7c7cc",
    accent:"#7c3aed", accentWarm:"#ea580c", accentRed:"#dc2626", accentGreen:"#059669",
    surface:"rgba(0,0,0,0.03)", navBg:"rgba(242,242,247,0.85)",
    shadow:"0 2px 20px rgba(0,0,0,0.07)", inputBg:"rgba(0,0,0,0.05)",
    chatUser:"rgba(124,58,237,0.1)", chatBot:"rgba(0,0,0,0.04)",
    gradient:"linear-gradient(135deg,#7c3aed,#ea580c)",
  }
};

const BUDGET_MAP = {goedkoop:{min:4,max:6},normaal:{min:6,max:10},luxe:{min:10,max:16}};
const CUISINES = ["Nederlands","Italiaans","Mexicaans","Thais","Indiaas","Chinees","Japans","Grieks","Marokkaans","Turks","Frans","Spaans","Amerikaans","Midden-Oosters","Vietnamees"];
const COMMON_ALLERGIES = ["Gluten","Lactose","Noten","Pinda's","Eieren","Soja","Vis","Schaaldieren","Sesamzaad","Kokos","Mosterd","Sulfieten"];
const EQUIPMENT = ["Oven","Magnetron","Airfryer","Frituurpan","Wok","BBQ / Grill","Slowcooker","Multicooker","Stoomoven","Sous-vide"];
const SK_CHATTIP = "wm_chatTip_v1";

export default function App() {
  const [isDark, setIsDark] = useState(()=>load(SK_THEME,true));
  const T = isDark ? THEMES.dark : THEMES.light;

  const [screen, setScreen] = useState("home");
  const [sideOpen, setSideOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState(()=>{
    const next7=getNext7Days().map(d=>d.name);
    const stored=load(SK_PREFS,{})?.days;
    if(stored?.length){const valid=stored.filter(d=>next7.includes(d));if(valid.length)return valid;}
    return next7.slice(0,5);
  });
  const [prefs, setPrefs] = useState(()=>({tempo:"mix",budget:"normaal",focus:"lekker",enummers:false,extra:"",recipeFrequentie:"geen",vasteDagen:{},favorites:[],...(load(SK_PREFS,{})?.prefs||{})}));
  const [memory, setMemory] = useState(()=>load(SK_MEM,{liked:[],disliked:[],notes:[]}));
  const [savedWeeks, setSavedWeeks] = useState(()=>load(SK_WEEKS,[]));
  const [account, setAccount] = useState(()=>({naam:"",email:"",personen:2,allergieen:"kokos",gezin:{volwassenen:2,kinderen:0,babies:0},keukens:[],allergieenList:[],nietsLekker:"",belangrijk:"",equipment:[],onboardingDone:false,...load(SK_ACC,{})}));
  const [menu, setMenu] = useState({});
  const [boodschappen, setBoodschappen] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [checked, setChecked] = useState({});
  const [view, setView] = useState("menu");
  const [recipe, setRecipe] = useState({});
  const [recipeDay, setRecipeDay] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [error, setError] = useState("");
  const [weekNr, setWeekNr] = useState(getWeekNr());
  const [isSaved, setIsSaved] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [swipeDay, setSwipeDay] = useState(null);
  const [user, setUser] = useState(()=>load(SK_USER,null));
  const [chatTipSeen, setChatTipSeen] = useState(()=>load(SK_CHATTIP,false));
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  const [pendingJoin, setPendingJoin] = useState(null);
  const [dayPersonen, setDayPersonen] = useState({});
  const [memProcessLoading, setMemProcessLoading] = useState(false);
  const [familyId, setFamilyId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const familyIdRef = useRef(null);
  const dbSaveTimeout = useRef(null);
  const [obData, setObData] = useState(()=>{const acc=load(SK_ACC,{});return {step:0,naam:acc.naam||"",gezin:acc.gezin||{volwassenen:2,kinderen:0,babies:0},keukens:acc.keukens||[],allergieenList:acc.allergieenList||[],allergieenExtra:"",nietsLekker:acc.nietsLekker||"",belangrijk:acc.belangrijk||"",equipment:acc.equipment||[]};});
  const touchStartX = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Device-only prefs
  useEffect(()=>{save(SK_THEME,isDark);},[isDark]);
  useEffect(()=>{save(SK_CHATTIP,chatTipSeen);},[chatTipSeen]);
  // localStorage als snelle cache
  useEffect(()=>{save(SK_PREFS,{days:selectedDays,prefs});},[selectedDays,prefs]);
  useEffect(()=>{save(SK_ACC,account);},[account]);
  const getDayPersonen = d => dayPersonen[d] ?? (account.personen || 2);
  useEffect(()=>{if(chatOpen)setTimeout(()=>chatEndRef.current?.scrollIntoView({behavior:"smooth"}),60);},[chatHistory,chatLoading,chatOpen]);
  // Houd familyIdRef gesynchroniseerd
  useEffect(()=>{familyIdRef.current=familyId;},[familyId]);

  // Debounced save van account/prefs naar Supabase
  useEffect(()=>{
    if(!familyIdRef.current) return;
    clearTimeout(dbSaveTimeout.current);
    dbSaveTimeout.current=setTimeout(()=>{
      saveFamily({
        naam:account.naam,personen:account.personen,gezin:account.gezin,
        keukens:account.keukens,allergieenlist:account.allergieenList,
        allergieen:account.allergieen,niets_lekker:account.nietsLekker,
        belangrijk:account.belangrijk,equipment:account.equipment,prefs,
        selected_days:selectedDays,
      });
    },1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[account,prefs,selectedDays]);

  // Supabase auth
  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session) handleSession(session); else setAuthLoading(false);
    });
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{
      if(session) handleSession(session);
      else{setUser(null);setFamilyId(null);setAuthLoading(false);}
    });
    return()=>subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Realtime sync
  useEffect(()=>{
    if(!familyId) return;
    const channel=supabase.channel(`family:${familyId}`)
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'families',filter:`id=eq.${familyId}`},(payload)=>{
        const f=payload.new;
        setAccount(a=>({...a,naam:f.naam||a.naam,personen:f.personen||a.personen,gezin:f.gezin||a.gezin,keukens:f.keukens||a.keukens,allergieenList:f.allergieenlist||a.allergieenList,allergieen:f.allergieen||a.allergieen,nietsLekker:f.niets_lekker||a.nietsLekker,belangrijk:f.belangrijk||a.belangrijk,equipment:f.equipment||a.equipment}));
        setPrefs(p=>({...p,...(f.prefs||{})}));
        if(f.memory) setMemory(f.memory);
        if(f.menu) setMenu(f.menu);
        if(f.boodschappen) setBoodschappen(f.boodschappen);
        if(f.budgets) setBudgets(f.budgets);
        if(f.selected_days) setSelectedDays(f.selected_days);
      })
      .subscribe();
    return()=>supabase.removeChannel(channel);
  },[familyId]);

  async function processToMemory(nietsLekker, allergieenExtra, belangrijk, extra="") {
    if(!nietsLekker && !allergieenExtra && !belangrijk && !extra) return;
    setMemProcessLoading(true);
    const prompt=`Verwerk smaakinput naar geheugenformaat. ALLEEN geldig JSON teruggeven.

Niet lekker: "${nietsLekker||""}"
Overige allergieën: "${allergieenExtra||""}"
Belangrijk bij eten: "${belangrijk||""}"
Extra wensen: "${extra||""}"

- disliked: losse ingrediënten/gerechten die niet lekker zijn of allergieën (korte termen, bijv. "lever")
- liked: positieve smaakvereisten/voorkeuren (bijv. "weinig vlees", "seizoensgebonden")
- notes: overkoepelende eetnotities (max 2 korte zinnen)
Lege arrays zijn prima als er niets past.

{"disliked":[],"liked":[],"notes":[]}`;
    try {
      const res=await fetch("/api/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:400,messages:[{role:"user",content:prompt}]})});
      const data=await res.json();
      const text=data.content.map(i=>i.text||"").join("");
      const parsed=JSON.parse(text.replace(/```json\s*/g,"").replace(/```\s*/g,"").trim());
      setMemory(m=>{
        const u={liked:[...new Set([...m.liked,...(parsed.liked||[])])],disliked:[...new Set([...m.disliked,...(parsed.disliked||[])])],notes:[...m.notes.slice(-8),...(parsed.notes||[])]};
        saveFamily({memory:u}); return u;
      });
    } catch(e){console.error(e);}
    setMemProcessLoading(false);
  }

  async function deleteAccount(){
    if(familyId&&user?.sub){
      const{data:members}=await supabase.from('profiles').select('id').eq('family_id',familyId);
      if(members?.length<=1) await supabase.from('families').delete().eq('id',familyId);
      else await supabase.from('profiles').update({family_id:null}).eq('id',user.sub);
    }
    await supabase.auth.signOut();
    [SK_PREFS,SK_MEM,SK_WEEKS,SK_ACC,SK_THEME,SK_USER,SK_CHATTIP].forEach(k=>{try{localStorage.removeItem(k);}catch{}});
    setUser(null); setFamilyId(null);
    setAccount({naam:"",email:"",personen:2,allergieen:"kokos",gezin:{volwassenen:2,kinderen:0,babies:0},keukens:[],allergieenList:[],nietsLekker:"",belangrijk:"",equipment:[],onboardingDone:false});
    setMemory({liked:[],disliked:[],notes:[]}); setSavedWeeks([]);
    setPrefs({tempo:"mix",budget:"normaal",focus:"lekker",enummers:false,extra:"",recipeFrequentie:"geen",vasteDagen:{},favorites:[]});
    setMenu({}); setBoodschappen([]); setBudgets({}); setChecked({}); setChatHistory([]);
    setObData({step:0,naam:"",gezin:{volwassenen:2,kinderen:0,babies:0},keukens:[],allergieenList:[],allergieenExtra:"",nietsLekker:"",belangrijk:"",equipment:[]});
    setShowDeleteConfirm(false); setScreen("home");
  }

  function generateShareUrl(){
    return `${window.location.origin}${window.location.pathname}?join=${familyId}`;
  }
  async function applyJoin(joinFamilyId){
    if(!user?.sub) return;
    await supabase.from('profiles').update({family_id:joinFamilyId}).eq('id',user.sub);
    setFamilyId(joinFamilyId);
    await loadFamilyData(joinFamilyId);
    setPendingJoin(null);
    setScreen("home");
  }

  function resetOnboarding(){
    setMemory({liked:[],disliked:[],notes:[]});
    setObData({step:0,naam:account.naam||"",gezin:account.gezin||{volwassenen:2,kinderen:0,babies:0},keukens:account.keukens||[],allergieenList:account.allergieenList||[],allergieenExtra:"",nietsLekker:account.nietsLekker||"",belangrijk:account.belangrijk||"",equipment:account.equipment||[]});
    setAccount(a=>({...a,onboardingDone:false}));
    setShowResetConfirm(false);
  }

  async function signInWithGoogle(){
    await supabase.auth.signInWithOAuth({provider:'google',options:{redirectTo:window.location.origin}});
  }
  async function handleSignOut(){
    await supabase.auth.signOut();
    setUser(null); setFamilyId(null);
  }
  async function handleSession(session){
    const u=session.user;
    setUser({name:u.user_metadata.full_name||u.user_metadata.name||"",email:u.email,picture:u.user_metadata.avatar_url||u.user_metadata.picture||"",sub:u.id});
    const {data:profile}=await supabase.from('profiles').select('family_id').eq('id',u.id).single();
    const params=new URLSearchParams(window.location.search);
    const joinFid=params.get('join');
    if(joinFid){window.history.replaceState({},"",window.location.pathname);setPendingJoin(joinFid);}
    if(profile?.family_id){
      setFamilyId(profile.family_id);
      await loadFamilyData(profile.family_id);
    } else if(!profile?.family_id&&!joinFid){
      const{data:family}=await supabase.from('families').insert({naam:u.user_metadata.full_name||u.user_metadata.name||'',personen:2}).select().single();
      if(family){
        await supabase.from('profiles').update({family_id:family.id,naam:u.user_metadata.full_name||'',avatar_url:u.user_metadata.avatar_url||''}).eq('id',u.id);
        setFamilyId(family.id);
        setAccount(a=>({...a,naam:u.user_metadata.full_name||u.user_metadata.name||a.naam,email:u.email,onboardingDone:false}));
      }
    }
    setAuthLoading(false);
  }
  async function loadFamilyData(fid){
    const{data:f}=await supabase.from('families').select('*').eq('id',fid).single();
    if(!f) return;
    setAccount(a=>({...a,naam:f.naam||a.naam,personen:f.personen||a.personen,gezin:f.gezin||a.gezin,keukens:f.keukens||a.keukens,allergieenList:f.allergieenlist||a.allergieenList,allergieen:f.allergieen||a.allergieen,nietsLekker:f.niets_lekker||a.nietsLekker,belangrijk:f.belangrijk||a.belangrijk,equipment:f.equipment||a.equipment,onboardingDone:!!f.naam}));
    setPrefs(p=>({...p,...(f.prefs||{})}));
    if(f.memory?.liked) setMemory(f.memory);
    if(f.menu&&Object.keys(f.menu).length>0){setMenu(f.menu);setBoodschappen(f.boodschappen||[]);setBudgets(f.budgets||{});}
    if(f.selected_days?.length) setSelectedDays(f.selected_days);
    const{data:weeks}=await supabase.from('saved_weeks').select('*').eq('family_id',fid).order('week_nr');
    if(weeks) setSavedWeeks(weeks.map(w=>({id:w.id,weekNr:w.week_nr,jaar:w.jaar,datum:w.datum,menu:w.menu,boodschappen:w.boodschappen,budgets:w.budgets,days:w.days})));
  }
  async function saveFamily(updates){
    const fid=familyIdRef.current; if(!fid) return;
    await supabase.from('families').update(updates).eq('id',fid);
  }

  const toggleDay = d => setSelectedDays(p=>p.includes(d)?p.filter(x=>x!==d):[...p,d]);
  const setPref = (k,v) => setPrefs(p=>({...p,[k]:v}));
  const toggleFavorite = (naam) => setPrefs(p=>({...p,favorites:(p.favorites||[]).includes(naam)?(p.favorites||[]).filter(f=>f!==naam):[...(p.favorites||[]),naam]}));
  const memCtx = () => { const p=[]; if(memory.liked.length)p.push(`Lekker: ${memory.liked.join(", ")}.`); if(memory.disliked.length)p.push(`NIET lekker: ${memory.disliked.join(", ")}.`); if(memory.notes.length)p.push(`Notities: ${memory.notes.slice(-4).join("; ")}.`); return p.join(" "); };
  const pastCtx = () => { const g=savedWeeks.slice(-4).flatMap(w=>Object.values(w.menu||{}).map(m=>m.naam)).filter(Boolean); return g.length?`Niet herhalen: ${g.join(", ")}.`:""; };

  const menuDays = DAYS.filter(d=>selectedDays.includes(d)&&menu[d]);
  const handleTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = e => {
    if(touchStartX.current===null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if(Math.abs(dx)<50) return;
    const cur = swipeDay ?? menuDays[0];
    const idx = menuDays.indexOf(cur);
    if(dx<0 && idx<menuDays.length-1) setSwipeDay(menuDays[idx+1]);
    if(dx>0 && idx>0) setSwipeDay(menuDays[idx-1]);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{if(menuDays.length>0&&!swipeDay) setSwipeDay(menuDays[0]);},[menu]);

  async function generateMenu() {
    setLoading(true); setError(""); setMenu({}); setBoodschappen([]); setBudgets({});
    setChecked({}); setChatHistory([]); setRecipe({}); setRecipeDay(null);
    setChatOpen(false); setScreen("result"); setView("menu"); setIsSaved(false);
    const wk=getWeekNr(); setWeekNr(wk);
    const dagPersonenStr=selectedDays.map(d=>`${d} (${getDayPersonen(d)} pers.)`).join(", ");
    const alg=[...(account.allergieenList||[]),account.allergieen].filter(Boolean).join(", ")||"kokos";
    const keukensTxt=account.keukens?.length?`Voorkeur keukens: ${account.keukens.join(", ")}.`:"";
    const nietsLekkerTxt=account.nietsLekker?`Absoluut niet lekker: ${account.nietsLekker}.`:"";
    const belangrijkTxt=account.belangrijk?`Belangrijk bij eten: ${account.belangrijk}.`:"";
    const equipmentTxt=account.equipment?.length?`Beschikbare apparatuur: ${account.equipment.join(", ")}. Gebruik ALLEEN deze apparatuur in recepten.`:"Standaard apparatuur (oven, kookplaat, magnetron).";
    const tTxt={snel:"snelle maaltijden (max 30 min)",culinair:"uitgebreide gerechten",mix:"mix snel+culinair"}[prefs.tempo];
    const bTxt={goedkoop:"goedkoop max €5pp",normaal:"normaal €5-10pp",luxe:"luxe €10+pp"}[prefs.budget];
    const fTxt={lekker:"focus lekker",gezond:"focus gezond",mix:"balans lekker+gezond"}[prefs.focus];
    const vasteDagenActief=Object.entries(prefs.vasteDagen||{}).filter(([d,v])=>selectedDays.includes(d)&&v.trim());
    const vasteDagenTxt=vasteDagenActief.length?`VERPLICHT voor vaste dagen: ${vasteDagenActief.map(([d,v])=>`${d}=${v}`).join(", ")}.`:"";
    const freqTxt={"1week":"Geen enkel gerecht meermaals in dezelfde week herhalen.","2weken":"Geen enkel gerecht herhalen t.o.v. de vorige week.","1maand":"Geen enkel gerecht herhalen uit de afgelopen 4 weken."}[prefs.recipeFrequentie]||"";
    const favTxt=(prefs.favorites||[]).length?`Favoriete gerechten die mogen terugkomen: ${prefs.favorites.join(", ")}.`:"";
    const dagJ=selectedDays.map(d=>`    "${d}":{"naam":"...","beschrijving":"...","tijd":"... min","keuken":"...","budget_pp":0.0}`).join(",\n");
    const prompt=`Maak uniek gevarieerd weekmenu week ${wk}: ${dagPersonenStr}. Stem boodschappenporties af op het aantal personen per dag.
Verboden: ${alg}.${prefs.enummers?" Geen E-nummers.":""} ${nietsLekkerTxt} ${prefs.extra?`Extra: ${prefs.extra}.`:""}
${tTxt}, ${bTxt}, ${fTxt}. ${keukensTxt} ${belangrijkTxt} ${equipmentTxt} ${memCtx()} ${pastCtx()} ${vasteDagenTxt} ${freqTxt} ${favTxt}
Maximale variatie, verschillende keukens. budget_pp = realistisch geschat bedrag in euros.

ALLEEN geldig JSON:
{"menu":{
${dagJ}
},"boodschappen":[
{"categorie":"Groenten & Fruit","items":["ui (2 st)"]},
{"categorie":"Vlees & Vis","items":["kipfilet (400g)"]},
{"categorie":"Zuivel & Eieren","items":["eieren (4 st)"]},
{"categorie":"Pasta, Rijst & Granen","items":["pasta (500g)"]},
{"categorie":"Kruiden & Sauzen","items":["paprikapoeder"]},
{"categorie":"Overig","items":["tomatenpuree (1 blik)"]}
]}`;
    const msgs=["Keukens uitzoeken...","Gerechten samenstellen...","Variatie checken...","Boodschappen berekenen...","Bijna klaar..."];
    let mi=0; setLoadingMsg(msgs[0]);
    const iv=setInterval(()=>{mi=(mi+1)%msgs.length;setLoadingMsg(msgs[mi]);},2000);
    try {
      const res=await fetch("/api/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:2500,messages:[{role:"user",content:prompt}]})});
      const rawText=await res.text();
      if(!res.ok) throw new Error(`API fout ${res.status}: ${rawText.slice(0,200)}`);
      const data=JSON.parse(rawText);
      if(data.error) throw new Error(data.error.message);
      const text=data.content.map(i=>i.text||"").join("");
      const parsed=JSON.parse(text.replace(/```json\s*/g,"").replace(/```\s*/g,"").trim());
      const m=parsed.menu||{};
      setMenu(m); setBoodschappen(parsed.boodschappen||[]);
      const bd={}; Object.entries(m).forEach(([d,v])=>{bd[d]=v.budget_pp||BUDGET_MAP[prefs.budget].min;});
      setBudgets(bd);
      setSwipeDay(Object.keys(m)[0]||null);
      saveFamily({menu:m,boodschappen:parsed.boodschappen||[],budgets:bd,selected_days:selectedDays});
    } catch(err){setError("Fout: "+err.message);setScreen("home");}
    clearInterval(iv); setLoading(false);
  }

  async function loadRecipe(day) {
    if(recipe[day]){setRecipeDay(day);return;} setRecipeDay(day); setRecipeLoading(true);
    const meal=menu[day]; const p=getDayPersonen(day);
    const prompt=`Volledig recept "${meal.naam}" voor ${p} personen. Geen kokos.${prefs.enummers?" Geen E-nummers.":""}
ALLEEN geldig JSON:
{"naam":"${meal.naam}","tijd":"${meal.tijd}","porties":${p},"ingredienten":[{"naam":"...","hoeveelheid":"..."}],"stappen":["..."],"tip":"...","keuken":"${meal.keuken||""}"}`;
    try {
      const res=await fetch("/api/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:1200,messages:[{role:"user",content:prompt}]})});
      const data=await res.json();
      const text=data.content.map(i=>i.text||"").join("");
      setRecipe(p=>({...p,[day]:JSON.parse(text.replace(/```json\s*/g,"").replace(/```\s*/g,"").trim())}));
    } catch(e){console.error(e);}
    setRecipeLoading(false);
  }

  async function sendChat() {
    if(!chatInput.trim()||chatLoading) return;
    const userMsg=chatInput.trim(); setChatInput(""); setChatLoading(true);
    const newH=[...chatHistory,{role:"user",content:userMsg}]; setChatHistory(newH);
    const sys=`Menu-assistent. Huidig bekeken dag: ${swipeDay||"onbekend"}. Menu: ${JSON.stringify(menu)}. Boodschappen: ${JSON.stringify(boodschappen)}. ${memCtx()} Geen kokos.${prefs.enummers?" Geen E-nummers.":""}
Bij wijziging: VOLLEDIG menu+boodschappen teruggeven. budget_pp per gerecht toevoegen. Als de gebruiker "deze", "dit gerecht", "niet lekker" of iets dergelijks zegt zonder een dag te noemen, vervang dan ALLEEN de huidige dag (${swipeDay||""}), niet de andere dagen.
ALTIJD ALLEEN JSON:
{"menu":{...},"boodschappen":[...],"antwoord":"...","memory_update":{"liked":[],"disliked":[],"note":""}}
Alleen vraag: {"antwoord":"...","memory_update":null}`;
    try {
      const res=await fetch("/api/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:2500,system:sys,messages:newH.map(m=>({role:m.role,content:m.content}))})});
      const data=await res.json();
      const text=data.content.map(i=>i.text||"").join("");
      const parsed=JSON.parse(text.replace(/```json\s*/g,"").replace(/```\s*/g,"").trim());
      const newMenu=parsed.menu||null; const newBood=parsed.boodschappen||null;
      if(newMenu){const bd={};Object.entries(newMenu).forEach(([d,v])=>{bd[d]=v.budget_pp||BUDGET_MAP[prefs.budget].min;});setMenu(newMenu);setBudgets(bd);setRecipe({});setIsSaved(false);saveFamily({menu:newMenu,boodschappen:newBood||boodschappen,budgets:bd});}
      if(newBood) setBoodschappen(newBood);
      if(parsed.memory_update){setMemory(p=>{const u={...p};if(parsed.memory_update.liked?.length)u.liked=[...new Set([...u.liked,...parsed.memory_update.liked])];if(parsed.memory_update.disliked?.length)u.disliked=[...new Set([...u.disliked,...parsed.memory_update.disliked])];if(parsed.memory_update.note)u.notes=[...u.notes.slice(-9),parsed.memory_update.note];saveFamily({memory:u});return u;});}
      setChatHistory([...newH,{role:"assistant",content:parsed.antwoord||"Gedaan!"}]);
    } catch(err){setChatHistory([...newH,{role:"assistant",content:"Fout: "+err.message}]);}
    setChatLoading(false);
  }

  async function saveWeek(){
    const fid=familyIdRef.current;
    const w={weekNr,jaar:new Date().getFullYear(),datum:new Date().toLocaleDateString("nl-NL"),menu,boodschappen,budgets,days:selectedDays};
    setSavedWeeks(p=>{const f=p.filter(x=>!(x.weekNr===w.weekNr&&x.jaar===w.jaar));return [...f,w].sort((a,b)=>a.weekNr-b.weekNr);});
    setIsSaved(true);
    if(fid){
      await supabase.from('saved_weeks').delete().match({family_id:fid,week_nr:weekNr,jaar:w.jaar});
      await supabase.from('saved_weeks').insert({family_id:fid,week_nr:weekNr,jaar:w.jaar,datum:w.datum,menu,boodschappen,budgets,days:selectedDays});
    }
  }
  function loadWeek(w){setMenu(w.menu);setBoodschappen(w.boodschappen);setBudgets(w.budgets||{});setSelectedDays(w.days||DAYS);setWeekNr(w.weekNr);setRecipe({});setChatHistory([]);setChatOpen(false);setChecked({});setIsSaved(true);setScreen("result");setView("menu");setSideOpen(false);setSwipeDay(Object.keys(w.menu||{})[0]||null);}
  function toggleCheck(cat,item){const k=`${cat}::${item}`;setChecked(p=>({...p,[k]:!p[k]}));}

  function printPlanner(){
    const personen=account.personen||2;
    const totalBudget=Object.entries(budgets).reduce((a,[d,b])=>a+b*getDayPersonen(d),0);
    const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Weekmenu Week ${weekNr}</title><style>
*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Inter',system-ui,sans-serif;background:#fff;color:#1c1c1e;padding:2cm;}
h1{font-size:1.8rem;font-weight:800;color:#1c1c1e;margin-bottom:0.2rem;letter-spacing:-0.03em;}
.sub{font-size:0.85rem;color:#6e6e73;margin-bottom:1.5rem;}
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:0.75rem;margin-bottom:2rem;}
.day{background:#f2f2f7;border:1px solid #e5e5ea;border-radius:12px;padding:0.85rem;}
.day-name{font-size:0.62rem;text-transform:uppercase;letter-spacing:0.08em;color:#6e6e73;font-weight:600;margin-bottom:0.3rem;}
.meal{font-size:0.95rem;font-weight:700;color:#1c1c1e;margin-bottom:0.2rem;}
.detail{font-size:0.76rem;color:#6e6e73;}
.budget-badge{display:inline-block;background:linear-gradient(135deg,#7c3aed,#ea580c);color:#fff;border-radius:50px;padding:0.15rem 0.5rem;font-size:0.7rem;font-weight:700;margin-top:0.35rem;}
h2{font-size:1rem;font-weight:700;color:#1c1c1e;margin:1.5rem 0 0.7rem;letter-spacing:-0.02em;}
.bood-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:1rem;}
.cat-name{font-size:0.62rem;text-transform:uppercase;letter-spacing:0.08em;color:#6e6e73;font-weight:600;margin-bottom:0.35rem;}
.item{font-size:0.82rem;padding:0.22rem 0;border-bottom:1px solid #f2f2f7;display:flex;align-items:center;gap:0.4rem;}
.check{width:12px;height:12px;border:1.5px solid #c7c7cc;border-radius:3px;flex-shrink:0;}
.budget-box{background:#f2f2f7;border-radius:12px;padding:0.75rem 1rem;margin-bottom:1.5rem;display:inline-block;}
.budget-box span{font-size:1.4rem;font-weight:800;color:#7c3aed;letter-spacing:-0.02em;}
@media print{body{padding:1.5cm;}}</style></head><body>
<h1>Weekmenu — Week ${weekNr}</h1>
<div class="sub">${new Date().toLocaleDateString("nl-NL",{weekday:"long",year:"numeric",month:"long",day:"numeric"})} · ${personen} personen</div>
<div class="budget-box">Geschat weekbudget: <span>€${totalBudget.toFixed(2)}</span></div>
<div class="grid">
${DAYS.filter(d=>selectedDays.includes(d)&&menu[d]).map(d=>{const m=menu[d];const b=budgets[d]||0;const dp=getDayPersonen(d);return`<div class="day"><div class="day-name">${d}</div><div class="meal">${m.naam}</div><div class="detail">${m.tijd}${m.keuken?` · ${m.keuken}`:""}</div><div class="budget-badge">€${(b*dp).toFixed(2)}</div></div>`;}).join("")}
</div>
<h2>Boodschappenlijst</h2>
<div class="bood-grid">
${(Array.isArray(boodschappen)?boodschappen:[]).map(cat=>`<div><div class="cat-name">${cat.categorie}</div>${cat.items.map(item=>`<div class="item"><div class="check"></div>${item}</div>`).join("")}</div>`).join("")}
</div>
</body></html>`;
    const win=window.open("","_blank"); win.document.write(html); win.document.close(); win.print();
  }

  const totalItems=Array.isArray(boodschappen)?boodschappen.reduce((a,c)=>a+c.items.length,0):0;
  const checkedCount=Object.values(checked).filter(Boolean).length;
  const personen=account.personen||2;
  const totalBudget=Object.entries(budgets).reduce((a,[d,b])=>a+b*getDayPersonen(d),0);
  const menuDaysOrdered=DAYS.filter(d=>selectedDays.includes(d)&&menu[d]);

  const F = "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',Roboto,sans-serif";
  const sw = 1.75; // icon strokeWidth

  const css = {
    wrap:{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:F,maxWidth:"480px",margin:"0 auto",display:"flex",flexDirection:"column",transition:"background 0.3s,color 0.3s"},
    hdr:{padding:"0.85rem 1.2rem",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:"0.75rem",flexShrink:0,background:T.navBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",position:"sticky",top:0,zIndex:50},
    title:{margin:0,fontSize:"1.05rem",fontWeight:"700",color:T.text,letterSpacing:"-0.02em"},
    sub:{margin:"0.05rem 0 0",fontSize:"0.6rem",color:T.textSoft,letterSpacing:"0.1em",textTransform:"uppercase",fontWeight:"500"},
    lbl:{fontSize:"0.67rem",color:T.textSoft,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.5rem",display:"block",fontWeight:"600"},
    chip:(on)=>({display:"inline-flex",alignItems:"center",gap:"0.3rem",padding:"0.42rem 0.9rem",borderRadius:"100px",border:on?`1.5px solid ${T.accent}`:`1px solid ${T.border}`,background:on?`${T.accent}18`:T.surface,color:on?T.accent:T.textSoft,cursor:"pointer",fontSize:"0.82rem",fontWeight:on?"600":"400",margin:"0.18rem",transition:"all 0.15s",fontFamily:F}),
    dayBtn:(on)=>({flex:1,padding:"0.55rem 0.05rem",borderRadius:"10px",border:on?`1.5px solid ${T.accent}`:`1px solid ${T.border}`,background:on?`${T.accent}15`:"transparent",color:on?T.accent:T.textDim,cursor:"pointer",fontSize:"0.69rem",fontWeight:on?"700":"400",textAlign:"center",transition:"all 0.15s"}),
    bigBtn:(off)=>({width:"100%",padding:"0.95rem",borderRadius:"14px",border:"none",background:off?T.cardAlt:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,color:off?T.textDim:"#fff",fontSize:"0.95rem",fontWeight:"700",cursor:off?"not-allowed":"pointer",transition:"all 0.2s",letterSpacing:"-0.01em",boxShadow:off?"none":`0 8px 24px ${T.accent}30`,fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.5rem"}),
    tab:(on)=>({flex:1,padding:"0.7rem",background:"none",border:"none",borderBottom:on?`2px solid ${T.accent}`:"2px solid transparent",color:on?T.accent:T.textDim,fontSize:"0.7rem",fontWeight:on?"600":"400",cursor:"pointer",letterSpacing:"0.05em",transition:"color 0.15s",textTransform:"uppercase",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.35rem"}),
    inp:{width:"100%",background:T.inputBg,border:`1.5px solid ${T.border}`,borderRadius:"12px",padding:"0.72rem 1rem",color:T.text,fontSize:"0.88rem",outline:"none",boxSizing:"border-box",transition:"border-color 0.2s",fontFamily:F},
    card:(hi)=>({background:hi?`${T.accent}0a`:T.card,border:hi?`1.5px solid ${T.accent}35`:`1px solid ${T.border}`,borderRadius:"18px",boxShadow:T.shadow,transition:"all 0.2s"}),
    navBtn:(on)=>({flex:1,padding:"0.65rem 0.2rem 0.55rem",background:"none",border:"none",display:"flex",flexDirection:"column",alignItems:"center",gap:"0.2rem",cursor:"pointer",color:on?T.accent:T.textDim,fontSize:"0.58rem",fontWeight:on?"600":"400",letterSpacing:"0.02em",borderTop:on?`2px solid ${T.accent}`:"2px solid transparent",transition:"all 0.15s",fontFamily:F}),
    iconBtn:(active)=>({background:active?`${T.accent}18`:T.surface,border:`1px solid ${active?T.accent+"40":T.border}`,borderRadius:"10px",padding:"0.4rem 0.55rem",color:active?T.accent:T.textSoft,cursor:"pointer",transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}),
  };

  const pill = (txt,color) => ({
    display:"inline-flex",alignItems:"center",gap:"0.3rem",padding:"0.25rem 0.65rem",
    borderRadius:"100px",background:`${color||T.accent}15`,color:color||T.accent,fontSize:"0.72rem",fontWeight:"600",
  });

  const BottomNav = () => (
    <div style={{display:"flex",borderTop:`1px solid ${T.border}`,background:T.navBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",flexShrink:0}}>
      {[
        [Home,"Home","home"],
        [UtensilsCrossed,"Menu","result"],
        [SlidersHorizontal,"Prefs","prefs"],
        [User,"Account","account"],
      ].map(([Icon,lb,sc])=>(
        <button key={sc} style={css.navBtn(screen===sc)} onClick={()=>setScreen(sc)}>
          <Icon size={20} strokeWidth={sw}/>
          <span>{lb}</span>
        </button>
      ))}
    </div>
  );

  const Header = ({title,sub,right}) => (
    <div style={css.hdr}>
      <button onClick={()=>setSideOpen(true)} style={css.iconBtn(false)}>
        <MenuIcon size={18} strokeWidth={sw}/>
      </button>
      <div style={{flex:1}}>
        <h1 style={css.title}>{title}</h1>
        {sub&&<p style={css.sub}>{sub}</p>}
      </div>
      <button onClick={()=>setIsDark(d=>!d)} style={css.iconBtn(false)} title="Thema">
        {isDark?<Sun size={17} strokeWidth={sw}/>:<Moon size={17} strokeWidth={sw}/>}
      </button>
      {right}
    </div>
  );

  const SideMenu = () => (
    <>
      <div onClick={()=>setSideOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,backdropFilter:"blur(4px)"}}/>
      <div style={{position:"fixed",top:0,left:0,bottom:0,width:"82%",maxWidth:"300px",background:T.card,zIndex:201,display:"flex",flexDirection:"column",borderRight:`1px solid ${T.border}`,overflowY:"auto"}}>
        <div style={{padding:"1.2rem 1.2rem 1rem",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.55rem"}}>
            <div style={{width:"32px",height:"32px",borderRadius:"50%",overflow:"hidden",flexShrink:0}}>
              {user?.picture
                ? <img src={user.picture} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                : <div style={{width:"100%",height:"100%",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <User size={14} strokeWidth={sw} color="#fff"/>
                  </div>
              }
            </div>
            <div>
              <div style={{fontSize:"0.95rem",fontWeight:"700",color:T.text,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:"0.4rem"}}>
                <UtensilsCrossed size={16} strokeWidth={sw} color={T.accent}/> Weekmenu
              </div>
              {(account.naam||user?.name)&&<div style={{fontSize:"0.76rem",color:T.textSoft,marginTop:"0.1rem"}}>Hoi, {account.naam||user?.name}!</div>}
            </div>
          </div>
          <button onClick={()=>setSideOpen(false)} style={{...css.iconBtn(false),borderRadius:"50%",width:"30px",height:"30px",padding:0}}>
            <X size={15} strokeWidth={sw}/>
          </button>
        </div>
        <div style={{padding:"0.8rem 0.75rem 0"}}>
          <div style={{...css.lbl,padding:"0 0.4rem",marginBottom:"0.4rem"}}>Navigatie</div>
          {[
            [Home,"Startscherm","home"],
            [UtensilsCrossed,"Huidig menu","result"],
            [SlidersHorizontal,"Voorkeuren","prefs"],
            [User,"Account","account"],
          ].map(([Icon,lb,sc])=>(
            <button key={sc} onClick={()=>{setScreen(sc);setSideOpen(false);}} style={{display:"flex",alignItems:"center",gap:"0.75rem",width:"100%",padding:"0.65rem 0.75rem",background:screen===sc?`${T.accent}12`:"none",border:"none",borderRadius:"12px",color:screen===sc?T.accent:T.textSoft,cursor:"pointer",fontSize:"0.88rem",textAlign:"left",marginBottom:"0.1rem",fontFamily:F,fontWeight:screen===sc?"600":"400"}}>
              <Icon size={16} strokeWidth={sw}/>{lb}
            </button>
          ))}
        </div>
        <div style={{padding:"0.5rem 0.75rem",flex:1}}>
          <div style={{...css.lbl,padding:"0.6rem 0.4rem 0.4rem",marginTop:"0.3rem",borderTop:`1px solid ${T.border}`,marginBottom:"0.4rem",display:"flex",alignItems:"center",gap:"0.35rem"}}>
            <Calendar size={11} strokeWidth={sw}/> Opgeslagen weken
          </div>
          {savedWeeks.length===0&&<p style={{color:T.textDim,fontSize:"0.78rem",padding:"0 0.4rem"}}>Nog niets opgeslagen.</p>}
          {savedWeeks.slice().reverse().map(w=>(
            <div key={`${w.jaar}-${w.weekNr}`} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"12px",padding:"0.65rem 0.8rem",marginBottom:"0.4rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{flex:1,cursor:"pointer"}} onClick={()=>loadWeek(w)}>
                  <div style={{color:T.accent,fontSize:"0.82rem",fontWeight:"700",letterSpacing:"-0.01em"}}>Week {w.weekNr} · {w.jaar}</div>
                  <div style={{color:T.textDim,fontSize:"0.7rem",marginTop:"0.06rem"}}>{w.datum}</div>
                  <div style={{color:T.textDim,fontSize:"0.7rem",marginTop:"0.15rem",lineHeight:"1.4"}}>{Object.values(w.menu||{}).slice(0,2).map(m=>m.naam).join(", ")}…</div>
                </div>
                <div style={{display:"flex",gap:"0.25rem",marginLeft:"0.4rem"}}>
                  <button onClick={()=>loadWeek(w)} style={{background:`${T.accent}15`,border:"none",borderRadius:"8px",padding:"0.28rem 0.55rem",color:T.accent,cursor:"pointer",display:"flex",alignItems:"center"}}>
                    <CornerUpLeft size={13} strokeWidth={sw}/>
                  </button>
                  <button onClick={()=>setSavedWeeks(p=>p.filter(x=>!(x.weekNr===w.weekNr&&x.jaar===w.jaar)))} style={{background:`${T.accentRed}10`,border:"none",borderRadius:"8px",padding:"0.28rem 0.5rem",color:T.accentRed,cursor:"pointer",display:"flex",alignItems:"center"}}>
                    <Trash2 size={13} strokeWidth={sw}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{padding:"0.85rem 1.2rem",borderTop:`1px solid ${T.border}`,fontSize:"0.72rem",color:T.textDim,display:"flex",alignItems:"center",gap:"0.35rem"}}>
          <Brain size={13} strokeWidth={sw}/> {memory.liked.length+memory.disliked.length} smaakvoorkeuren
        </div>
      </div>
    </>
  );

  const RecipeModal = () => {
    if(!recipeDay) return null;
    const r=recipe[recipeDay];
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:300,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setRecipeDay(null)}>
        <div style={{background:T.card,borderRadius:"24px 24px 0 0",width:"100%",maxWidth:"480px",maxHeight:"90vh",overflowY:"auto",border:`1px solid ${T.border}`,boxShadow:`0 -8px 40px rgba(0,0,0,0.3)`}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:"1rem 1.2rem 0"}}>
            <div style={{width:"36px",height:"4px",borderRadius:"100px",background:T.border,margin:"0 auto 1rem"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"1rem"}}>
              <div style={{flex:1}}>
                <div style={{...pill(),marginBottom:"0.4rem"}}>{recipeDay}{r?.keuken?` · ${r.keuken}`:""}</div>
                <h2 style={{margin:"0.3rem 0 0",fontSize:"1.2rem",fontWeight:"800",color:T.text,letterSpacing:"-0.02em",lineHeight:"1.2"}}>{r?.naam||menu[recipeDay]?.naam}</h2>
              </div>
              <button onClick={()=>setRecipeDay(null)} style={{...css.iconBtn(false),borderRadius:"50%",width:"32px",height:"32px",padding:0,flexShrink:0,marginLeft:"0.75rem"}}>
                <X size={15} strokeWidth={sw}/>
              </button>
            </div>
          </div>
          {recipeLoading?(
            <div style={{textAlign:"center",padding:"3rem",color:T.textSoft}}>
              <Loader2 size={28} className="spin" style={{margin:"0 auto 0.6rem",display:"block",color:T.accent}}/>
              <div style={{fontSize:"0.85rem"}}>Recept laden…</div>
            </div>
          ):r?(
            <div style={{padding:"0 1.2rem 2rem"}}>
              <div style={{display:"flex",gap:"0.5rem",marginBottom:"1.2rem",flexWrap:"wrap"}}>
                <span style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"10px",padding:"0.28rem 0.7rem",fontSize:"0.76rem",color:T.textSoft,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.3rem"}}>
                  <Clock size={12} strokeWidth={sw}/>{r.tijd}
                </span>
                <span style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"10px",padding:"0.28rem 0.7rem",fontSize:"0.76rem",color:T.textSoft,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.3rem"}}>
                  <Users size={12} strokeWidth={sw}/>{r.porties} personen
                </span>
              </div>
              <div style={{marginBottom:"1.2rem"}}>
                <div style={css.lbl}>Ingrediënten</div>
                <div style={{background:T.surface,borderRadius:"14px",border:`1px solid ${T.border}`,overflow:"hidden"}}>
                  {r.ingredienten?.map((ing,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"0.6rem 0.9rem",borderBottom:i<r.ingredienten.length-1?`1px solid ${T.border}`:"none",fontSize:"0.84rem"}}>
                      <span style={{color:T.text,fontWeight:"500",textTransform:"capitalize"}}>{ing.naam}</span>
                      <span style={{color:T.textSoft}}>{ing.hoeveelheid}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:"1.2rem"}}>
                <div style={css.lbl}>Bereiding</div>
                {r.stappen?.map((stap,i)=>(
                  <div key={i} style={{display:"flex",gap:"0.75rem",marginBottom:"0.65rem",alignItems:"flex-start"}}>
                    <div style={{width:"22px",height:"22px",borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.66rem",color:"#fff",fontWeight:"700",flexShrink:0,marginTop:"0.05rem"}}>{i+1}</div>
                    <p style={{margin:0,fontSize:"0.85rem",color:T.textSoft,lineHeight:"1.6"}}>{stap.replace(/^Stap \d+:\s*/i,"")}</p>
                  </div>
                ))}
              </div>
              {r.tip&&(
                <div style={{background:`${T.accentWarm}10`,border:`1px solid ${T.accentWarm}25`,borderRadius:"12px",padding:"0.75rem 0.9rem",fontSize:"0.82rem",color:T.accentWarm,lineHeight:"1.5",display:"flex",gap:"0.5rem",alignItems:"flex-start"}}>
                  <Lightbulb size={15} strokeWidth={sw} style={{flexShrink:0,marginTop:"0.1rem"}}/>
                  <span><strong>Tip:</strong> {r.tip}</span>
                </div>
              )}
            </div>
          ):null}
        </div>
      </div>
    );
  };

  const BudgetBadge = ({day}) => {
    const b=budgets[day]; if(!b) return null;
    return (
      <span style={{background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,borderRadius:"100px",padding:"0.2rem 0.6rem",fontSize:"0.7rem",color:"#fff",fontWeight:"700",display:"inline-flex",alignItems:"center",gap:"0.3rem"}}>
        <Coins size={11} strokeWidth={sw}/>€{(b*getDayPersonen(day)).toFixed(2)}<span style={{fontSize:"0.62rem",opacity:0.8,fontWeight:"500"}}> totaal</span>
      </span>
    );
  };

  const SwipeView = () => {
    const day=swipeDay||menuDaysOrdered[0]; if(!day||!menu[day]) return null;
    const meal=menu[day]; const idx=menuDaysOrdered.indexOf(day);
    return (
      <div style={{padding:"0.9rem 1.2rem 1rem",flex:1,display:"flex",flexDirection:"column"}} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div style={{display:"flex",gap:"0.35rem",overflowX:"auto",marginBottom:"1rem",paddingBottom:"0.1rem"}}>
          {menuDaysOrdered.map((d,i)=>(
            <button key={d} onClick={()=>setSwipeDay(d)} style={{padding:"0.28rem 0.8rem",borderRadius:"100px",border:"none",flexShrink:0,background:i===idx?T.accent:T.surface,color:i===idx?"#fff":T.textSoft,fontSize:"0.74rem",fontWeight:i===idx?"600":"400",cursor:"pointer",transition:"all 0.2s",fontFamily:F}}>{d}</button>
          ))}
        </div>
        <div style={{...css.card(false),padding:"1.4rem",flex:1,display:"flex",flexDirection:"column"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.85rem"}}>
            <span style={pill()}>{day}</span>
            <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
              <button onClick={()=>toggleFavorite(meal.naam)} title={(prefs.favorites||[]).includes(meal.naam)?"Verwijder uit favorieten":"Voeg toe aan favorieten"} style={{background:"none",border:"none",padding:"0.2rem",cursor:"pointer",display:"flex",alignItems:"center",color:(prefs.favorites||[]).includes(meal.naam)?"#f59e0b":T.textDim,transition:"color 0.15s,transform 0.15s",transform:(prefs.favorites||[]).includes(meal.naam)?"scale(1.15)":"scale(1)"}}>
                <Star size={18} strokeWidth={sw} fill={(prefs.favorites||[]).includes(meal.naam)?"currentColor":"none"}/>
              </button>
              <BudgetBadge day={day}/>
            </div>
          </div>
          <h2 style={{fontSize:"1.65rem",fontWeight:"800",color:T.text,margin:"0 0 0.5rem",lineHeight:"1.15",letterSpacing:"-0.03em"}}>{meal.naam}</h2>
          <div style={{display:"flex",gap:"0.4rem",marginBottom:"0.9rem",flexWrap:"wrap"}}>
            <span style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"0.25rem 0.65rem",fontSize:"0.75rem",color:T.textSoft,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.3rem"}}>
              <Clock size={12} strokeWidth={sw}/>{meal.tijd}
            </span>
            {meal.keuken&&(
              <span style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"0.25rem 0.65rem",fontSize:"0.75rem",color:T.textSoft,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.3rem"}}>
                <Globe size={12} strokeWidth={sw}/>{meal.keuken}
              </span>
            )}
          </div>
          <p style={{fontSize:"0.88rem",color:T.textSoft,lineHeight:"1.65",margin:"0 0 1.2rem",flex:1}}>{meal.beschrijving}</p>
          <button onClick={()=>loadRecipe(day)} style={{width:"100%",padding:"0.8rem",borderRadius:"12px",border:"none",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,color:"#fff",fontSize:"0.9rem",fontWeight:"700",cursor:"pointer",boxShadow:`0 6px 20px ${T.accent}35`,fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.5rem"}}>
            <BookOpen size={16} strokeWidth={sw}/> Bekijk recept
          </button>
        </div>
        <div style={{display:"flex",gap:"0.5rem",marginTop:"0.75rem"}}>
          <button onClick={()=>idx>0&&setSwipeDay(menuDaysOrdered[idx-1])} disabled={idx===0} style={{flex:1,background:idx===0?"transparent":T.card,border:`1px solid ${idx===0?"transparent":T.border}`,borderRadius:"12px",padding:"0.55rem 0.5rem",color:idx===0?T.textDim:T.textSoft,cursor:idx===0?"default":"pointer",fontSize:"0.79rem",fontFamily:F,boxShadow:idx===0?"none":T.shadow,transition:"all 0.15s",fontWeight:"500",display:"flex",alignItems:"center",justifyContent:"center",gap:"0.25rem"}}>
            <ChevronLeft size={15} strokeWidth={sw}/>{idx>0?menuDaysOrdered[idx-1]:""}
          </button>
          <button onClick={()=>idx<menuDaysOrdered.length-1&&setSwipeDay(menuDaysOrdered[idx+1])} disabled={idx===menuDaysOrdered.length-1} style={{flex:1,background:idx===menuDaysOrdered.length-1?"transparent":T.card,border:`1px solid ${idx===menuDaysOrdered.length-1?"transparent":T.border}`,borderRadius:"12px",padding:"0.55rem 0.5rem",color:idx===menuDaysOrdered.length-1?T.textDim:T.textSoft,cursor:idx===menuDaysOrdered.length-1?"default":"pointer",fontSize:"0.79rem",fontFamily:F,boxShadow:idx===menuDaysOrdered.length-1?"none":T.shadow,transition:"all 0.15s",fontWeight:"500",display:"flex",alignItems:"center",justifyContent:"center",gap:"0.25rem",textAlign:"right"}}>
            {idx<menuDaysOrdered.length-1?menuDaysOrdered[idx+1]:""}<ChevronRight size={15} strokeWidth={sw}/>
          </button>
        </div>
      </div>
    );
  };

  const OnboardingScreen = () => {
    const TOTAL=8;
    const {step,naam,gezin,keukens,allergieenList,allergieenExtra,nietsLekker,belangrijk,equipment}=obData;
    const toggleKeuken=k=>setObData(o=>({...o,keukens:o.keukens.includes(k)?o.keukens.filter(x=>x!==k):[...o.keukens,k]}));
    const toggleAllergie=a=>setObData(o=>({...o,allergieenList:o.allergieenList.includes(a)?o.allergieenList.filter(x=>x!==a):[...o.allergieenList,a]}));
    const toggleEquipment=e=>setObData(o=>({...o,equipment:o.equipment.includes(e)?o.equipment.filter(x=>x!==e):[...o.equipment,e]}));
    const finish=()=>{
      const totPers=(gezin.volwassenen||2)+(gezin.kinderen||0);
      const algStr=[...allergieenList,allergieenExtra].filter(Boolean).join(", ")||"kokos";
      setAccount(a=>({...a,naam:naam||a.naam,gezin,keukens,allergieenList,allergieen:algStr,nietsLekker,belangrijk,equipment,personen:totPers||2,onboardingDone:true}));
      processToMemory(nietsLekker, allergieenExtra, belangrijk);
    };
    const skip=()=>setAccount(a=>({...a,onboardingDone:true}));
    const next=()=>{if(step<TOTAL-1)setObData(o=>({...o,step:o.step+1}));else finish();};
    const prev=()=>setObData(o=>({...o,step:o.step-1}));
    const titles=["Jij & je gezin","Kookstijl","Keukens","Allergieën","Smaakprofiel","Jouw keuken","Gewoonten","Klaar!"];
    return (
      <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",fontFamily:F,maxWidth:"480px",margin:"0 auto"}}>
        <div style={{padding:"0.9rem 1.2rem",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:"0.75rem",background:T.navBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",position:"sticky",top:0,zIndex:10,flexShrink:0}}>
          {step>0?<button onClick={prev} style={css.iconBtn(false)}><ChevronLeft size={18} strokeWidth={sw}/></button>:<div style={{width:"34px"}}/>}
          <div style={{flex:1}}>
            <div style={{fontSize:"0.62rem",color:T.textSoft,letterSpacing:"0.1em",textTransform:"uppercase",fontWeight:"600",marginBottom:"0.1rem"}}>Stap {step+1} van {TOTAL}</div>
            <div style={{fontSize:"1rem",fontWeight:"700",color:T.text,letterSpacing:"-0.02em"}}>{titles[step]}</div>
          </div>
          <button onClick={skip} style={{background:"none",border:"none",color:T.textDim,fontSize:"0.78rem",cursor:"pointer",fontFamily:F,padding:"0.3rem 0"}}>Overslaan</button>
        </div>
        <div style={{height:"3px",background:T.border,flexShrink:0}}>
          <div style={{height:"100%",background:`linear-gradient(90deg,${T.accent},${T.accentWarm})`,width:`${((step+1)/TOTAL)*100}%`,transition:"width 0.35s",borderRadius:"0 2px 2px 0"}}/>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"1.4rem 1.2rem 1rem"}}>
          {step===0&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Welkom! Laten we je profiel even instellen zodat we het perfecte weekmenu voor je gezin kunnen maken.</p>
            <div style={{marginBottom:"1.1rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><User size={11} strokeWidth={sw}/>Jouw naam</span>
              <input style={css.inp} placeholder="bijv. Familie de Jong" value={naam} onChange={e=>setObData(o=>({...o,naam:e.target.value}))}/>
            </div>
            <div style={{...css.card(false),padding:"1rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem",marginBottom:"0.8rem"}}><Users size={11} strokeWidth={sw}/>Waar bestaat het gezin uit?</span>
              {[["Volwassenen","volwassenen",6],["Kinderen","kinderen",5],["Baby's","babies",4]].map(([lbl,key,max])=>(
                <div key={key} style={{marginBottom:"0.75rem"}}>
                  <div style={{fontSize:"0.76rem",color:T.textSoft,marginBottom:"0.3rem",fontWeight:"500"}}>{lbl}</div>
                  <div style={{display:"flex",gap:"0.3rem",flexWrap:"wrap"}}>
                    {Array.from({length:max+1},(_,i)=>i).map(n=>(
                      <button key={n} style={{...css.chip(gezin[key]===n),minWidth:"38px",justifyContent:"center",padding:"0.38rem 0",margin:0}} onClick={()=>setObData(o=>({...o,gezin:{...o.gezin,[key]:n}}))}>{n}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>}
          {step===1&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Hoe kook je het liefst? Je kunt dit later altijd via de chat aanpassen.</p>
            {[
              ["Kooktijd","tempo",Clock,[["snel",[Zap,"Snel (≤30 min)"]],["mix",[Scale,"Mix"]],["culinair",[ChefHat,"Culinair"]]]],
              ["Budget p.p.","budget",Wallet,[["goedkoop",[Coins,"Goedkoop (≤€5 p.p.)"]],["normaal",[Wallet,"Normaal (€5–10 p.p.)"]],["luxe",[Gem,"Luxe (€10+ p.p.)"]]]],
              ["Focus","focus",Target,[["lekker",[Heart,"Lekker"]],["mix",[Scale,"Balans"]],["gezond",[Leaf,"Gezond"]]]],
              ["E-nummers","enummers",FlaskConical,[["false",[CircleCheck,"Maakt niet uit"]],["true",[CircleX,"Geen E-nummers"]]]],
            ].map(([label,key,LabelIcon,opts])=>(
              <div key={key} style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.7rem"}}>
                <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><LabelIcon size={11} strokeWidth={sw}/>{label}</span>
                <div>{opts.map(([v,[OptIcon,l]])=>(
                  <button key={v} style={css.chip(String(prefs[key])===v)} onClick={()=>setPref(key,key==="enummers"?v==="true":v)}>
                    <OptIcon size={13} strokeWidth={sw}/>{l}
                  </button>
                ))}</div>
              </div>
            ))}
          </>}
          {step===2&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Welke keukens vind je lekker? We gebruiken dit als inspiratie bij het plannen.</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem"}}>
              {CUISINES.map(k=>(
                <button key={k} style={css.chip(keukens.includes(k))} onClick={()=>toggleKeuken(k)}>{k}</button>
              ))}
            </div>
            <p style={{color:T.textDim,fontSize:"0.74rem",marginTop:"0.9rem",lineHeight:"1.4"}}>Geen selectie? Dan gebruiken we een mooie mix van keukens.</p>
          </>}
          {step===3&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Zijn er allergieën of intoleranties? We houden hier strict rekening mee.</p>
            <div style={{marginBottom:"1.2rem"}}>
              <span style={css.lbl}>Veelvoorkomende allergieën</span>
              <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem"}}>
                {COMMON_ALLERGIES.map(a=>(
                  <button key={a} style={css.chip(allergieenList.includes(a))} onClick={()=>toggleAllergie(a)}>{a}</button>
                ))}
              </div>
            </div>
            <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><PenLine size={11} strokeWidth={sw}/>Overige allergieën</span>
            <input style={css.inp} placeholder="bijv. kiwi, mango…" value={allergieenExtra} onChange={e=>setObData(o=>({...o,allergieenExtra:e.target.value}))}/>
          </>}
          {step===4&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Vertel ons iets meer over je smaak — zodat het menu echt bij jou past.</p>
            <div style={{marginBottom:"1rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><ThumbsDown size={11} strokeWidth={sw}/>Wat vind je absoluut niet lekker?</span>
              <textarea style={{...css.inp,resize:"none",minHeight:"72px",lineHeight:"1.6"}} placeholder="bijv. lever, witlof, sterke kazen…" value={nietsLekker} onChange={e=>setObData(o=>({...o,nietsLekker:e.target.value}))}/>
            </div>
            <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Heart size={11} strokeWidth={sw}/>Wat vind je belangrijk bij eten?</span>
            <textarea style={{...css.inp,resize:"none",minHeight:"72px",lineHeight:"1.6"}} placeholder="bijv. duurzaam, seizoensgebonden, weinig vlees…" value={belangrijk} onChange={e=>setObData(o=>({...o,belangrijk:e.target.value}))}/>
          </>}
          {step===5&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Welke apparaten gebruik je in de keuken? Zo stemmen we het menu af op wat je hebt.</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem",marginBottom:"1.2rem"}}>
              {EQUIPMENT.map(e=>(
                <button key={e} style={css.chip(equipment.includes(e))} onClick={()=>toggleEquipment(e)}>{e}</button>
              ))}
            </div>
            <p style={{color:T.textDim,fontSize:"0.74rem",lineHeight:"1.4"}}>Geen selectie? Dan gaan we uit van standaard apparatuur (oven, magnetron, kookplaat).</p>
          </>}
          {step===6&&<>
            <p style={{color:T.textSoft,fontSize:"0.86rem",marginBottom:"1.4rem",lineHeight:"1.55"}}>Stel in hoe vaak hetzelfde recept mag terugkomen en voeg vaste gerechten toe voor bepaalde dagen.</p>
            <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.7rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><RotateCcw size={11} strokeWidth={sw}/>Hoe vaak mag hetzelfde recept terugkomen?</span>
              <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem"}}>
                {[["geen","Geen beperking"],["1week","1× per week"],["2weken","1× per 2 weken"],["1maand","1× per maand"]].map(([v,l])=>(
                  <button key={v} style={css.chip((prefs.recipeFrequentie||"geen")===v)} onClick={()=>setPref("recipeFrequentie",v)}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{...css.card(false),padding:"0.9rem 1rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Calendar size={11} strokeWidth={sw}/>Vaste gerechten per dag <span style={{color:T.textDim,fontWeight:"400",fontSize:"0.69rem"}}>(optioneel)</span></span>
              <p style={{color:T.textDim,fontSize:"0.74rem",lineHeight:"1.4",margin:"0.2rem 0 0.8rem"}}>Bijv. elke zaterdag friet of elke zondag pasta.</p>
              {DAYS.map(dag=>(
                <div key={dag} style={{display:"flex",alignItems:"center",gap:"0.5rem",marginBottom:"0.35rem"}}>
                  <span style={{width:"80px",fontSize:"0.77rem",color:T.textSoft,fontWeight:"500",flexShrink:0}}>{dag}</span>
                  <input
                    style={{...css.inp,margin:0,flex:1,padding:"0.28rem 0.55rem",fontSize:"0.8rem"}}
                    placeholder="Vaste maaltijd…"
                    value={(prefs.vasteDagen||{})[dag]||""}
                    onChange={e=>setPref("vasteDagen",{...(prefs.vasteDagen||{}),[dag]:e.target.value})}
                  />
                </div>
              ))}
            </div>
          </>}
          {step===7&&(
            <div style={{textAlign:"center",paddingTop:"0.5rem"}}>
              <div style={{width:"64px",height:"64px",borderRadius:"20px",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 1rem",boxShadow:`0 8px 24px ${T.accent}40`}}>
                <Sparkles size={28} strokeWidth={sw} color="#fff"/>
              </div>
              <h2 style={{fontSize:"1.4rem",fontWeight:"800",color:T.text,letterSpacing:"-0.03em",margin:"0 0 0.5rem"}}>Alles ingesteld!</h2>
              <p style={{color:T.textSoft,fontSize:"0.86rem",margin:"0 auto 1.5rem",lineHeight:"1.5",maxWidth:"260px"}}>Via de chat kun je altijd je menu en voorkeuren aanpassen.</p>
              <div style={{...css.card(false),padding:"1rem",textAlign:"left"}}>
                {(naam||user?.name)&&<div style={{display:"flex",gap:"0.5rem",marginBottom:"0.5rem",fontSize:"0.83rem",alignItems:"center"}}><User size={14} strokeWidth={sw} color={T.accent}/><span style={{color:T.textSoft}}>{naam||user?.name} · {(gezin.volwassenen||2)+(gezin.kinderen||0)} personen</span></div>}
                {keukens.length>0&&<div style={{display:"flex",gap:"0.5rem",marginBottom:"0.5rem",fontSize:"0.83rem",alignItems:"center"}}><Globe size={14} strokeWidth={sw} color={T.accent}/><span style={{color:T.textSoft}}>{keukens.slice(0,4).join(", ")}{keukens.length>4?` +${keukens.length-4}`:""}</span></div>}
                {allergieenList.length>0&&<div style={{display:"flex",gap:"0.5rem",marginBottom:"0.5rem",fontSize:"0.83rem",alignItems:"center"}}><CircleX size={14} strokeWidth={sw} color={T.accentRed}/><span style={{color:T.textSoft}}>{allergieenList.join(", ")}</span></div>}
                {equipment.length>0&&<div style={{display:"flex",gap:"0.5rem",fontSize:"0.83rem",alignItems:"center"}}><Zap size={14} strokeWidth={sw} color={T.accentWarm}/><span style={{color:T.textSoft}}>{equipment.slice(0,4).join(", ")}{equipment.length>4?` +${equipment.length-4}`:""}</span></div>}
              </div>
            </div>
          )}
        </div>
        <div style={{padding:"1rem 1.2rem",borderTop:`1px solid ${T.border}`,background:T.navBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",flexShrink:0}}>
          <button onClick={next} style={css.bigBtn(false)}>
            {step===TOTAL-1?<><Sparkles size={18} strokeWidth={sw}/>Start met plannen!</>:<>Volgende<ChevronRight size={18} strokeWidth={sw}/></>}
          </button>
        </div>
      </div>
    );
  };

  const LoginScreen = () => (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem",fontFamily:F,position:"relative"}}>
      <button onClick={()=>setIsDark(d=>!d)} style={{position:"absolute",top:"1.2rem",right:"1.2rem",...css.iconBtn(false)}}>
        {isDark?<Sun size={17} strokeWidth={sw}/>:<Moon size={17} strokeWidth={sw}/>}
      </button>
      <div style={{width:"76px",height:"76px",borderRadius:"24px",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"1.2rem",boxShadow:`0 12px 32px ${T.accent}40`}}>
        <UtensilsCrossed size={34} strokeWidth={sw} color="#fff"/>
      </div>
      <h1 style={{fontSize:"2rem",fontWeight:"800",color:T.text,letterSpacing:"-0.04em",margin:"0 0 0.35rem",textAlign:"center"}}>Weekmenu</h1>
      <p style={{color:T.textSoft,fontSize:"0.9rem",margin:"0 0 2.5rem",textAlign:"center",maxWidth:"260px",lineHeight:"1.5"}}>Slimme maaltijdplanning voor je gezin</p>
      <div style={{width:"100%",maxWidth:"320px",display:"flex",flexDirection:"column",gap:"0.75rem"}}>
        <button onClick={signInWithGoogle} style={{width:"100%",padding:"0.9rem",borderRadius:"14px",border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:"0.92rem",cursor:"pointer",fontFamily:F,fontWeight:"600",display:"flex",alignItems:"center",justifyContent:"center",gap:"0.75rem",boxShadow:T.shadow,transition:"all 0.15s"}}>
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Inloggen met Google
        </button>
      </div>
    </div>
  );

  if(authLoading) return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <Loader2 size={32} className="spin" color={T.accent}/>
    </div>
  );
  if(!user) return <LoginScreen/>;
  if(!account.onboardingDone) return OnboardingScreen();

  // ── HOME ──────────────────────────────────────────────────────────────────
  if(screen==="home") return (
    <div style={css.wrap}>
      {sideOpen&&<SideMenu/>}
      <Header title="Weekmenu" sub={`Week ${getWeekNr()} · ${personen} personen`}/>
      <div style={{flex:1,overflowY:"auto",padding:"1.2rem"}}>
        {account.naam?(
          <div style={{background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,borderRadius:"18px",padding:"1.1rem 1.3rem",marginBottom:"1rem",boxShadow:`0 8px 24px ${T.accent}30`}}>
            <div style={{fontSize:"1rem",color:"#fff",fontWeight:"700",letterSpacing:"-0.01em"}}>Hoi {account.naam} 👋</div>
            <div style={{fontSize:"0.78rem",color:"rgba(255,255,255,0.7)",marginTop:"0.15rem"}}>Klaar voor week {getWeekNr()}?</div>
          </div>
        ):(
          <div onClick={()=>setScreen("account")} style={{...css.card(false),padding:"1rem 1.1rem",marginBottom:"1rem",cursor:"pointer",display:"flex",alignItems:"center",gap:"0.9rem"}}>
            <div style={{width:"40px",height:"40px",borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <User size={20} strokeWidth={sw} color="#fff"/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:"0.88rem",fontWeight:"600",color:T.text}}>Stel je profiel in</div>
              <div style={{fontSize:"0.75rem",color:T.textSoft,marginTop:"0.08rem"}}>Voeg je naam, personen en allergieën toe</div>
            </div>
            <ChevronRight size={18} strokeWidth={sw} color={T.textDim}/>
          </div>
        )}
        <div style={{...css.card(false),padding:"0.85rem 1.1rem",marginBottom:"1rem"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.5rem"}}>
            <div style={{...css.lbl,marginBottom:0,display:"flex",alignItems:"center",gap:"0.35rem"}}><Brain size={11} strokeWidth={sw}/>Smaakgeheugen</div>
            {(memory.liked.length===0&&memory.disliked.length===0)&&(
              <span style={{fontSize:"0.62rem",color:T.textDim,background:T.surface,border:`1px solid ${T.border}`,borderRadius:"100px",padding:"0.15rem 0.5rem",fontWeight:"500"}}>voorbeeld</span>
            )}
          </div>
          {memory.liked.length>0?(
            <div style={{fontSize:"0.78rem",color:T.textSoft,marginBottom:"0.12rem",display:"flex",alignItems:"center",gap:"0.35rem"}}>
              <ThumbsUp size={12} strokeWidth={sw} color={T.accentGreen}/>{memory.liked.slice(-3).join(", ")}
            </div>
          ):(
            <div style={{fontSize:"0.78rem",color:T.textDim,marginBottom:"0.12rem",fontStyle:"italic",display:"flex",alignItems:"center",gap:"0.35rem"}}>
              <ThumbsUp size={12} strokeWidth={sw}/>Pasta, Thaise curry, Stamppot
            </div>
          )}
          {memory.disliked.length>0?(
            <div style={{fontSize:"0.78rem",color:T.textSoft,display:"flex",alignItems:"center",gap:"0.35rem"}}>
              <ThumbsDown size={12} strokeWidth={sw} color={T.accentRed}/>{memory.disliked.slice(-3).join(", ")}
            </div>
          ):(
            <div style={{fontSize:"0.78rem",color:T.textDim,fontStyle:"italic",display:"flex",alignItems:"center",gap:"0.35rem"}}>
              <ThumbsDown size={12} strokeWidth={sw}/>Lever, Bloemkool, Witlof
            </div>
          )}
          {(memory.liked.length===0&&memory.disliked.length===0)&&(
            <div style={{fontSize:"0.72rem",color:T.textDim,marginTop:"0.4rem"}}>Wordt automatisch gevuld via de AI-chat</div>
          )}
        </div>
        <div style={{...css.card(false),padding:"1rem 1.1rem",marginBottom:"1.2rem"}}>
          <span style={css.lbl}>Voor welke dagen wil je boodschappen doen?</span>
          <div style={{display:"flex",gap:"0.2rem",marginBottom:"0.5rem"}}>
            {getNext7Days().map(({name,short})=>(
              <button key={name} style={css.dayBtn(selectedDays.includes(name))} onClick={()=>toggleDay(name)}>{short}</button>
            ))}
          </div>
          <p style={{color:T.textDim,fontSize:"0.74rem",margin:"0 0 0.1rem"}}>
            {selectedDays.length===0?"Selecteer minimaal 1 dag":
              getNext7Days().filter(d=>selectedDays.includes(d.name)).map(d=>d.isToday?`Vandaag ${d.num} ${d.month}`:`${d.short} ${d.num} ${d.month}`).join(", ")
            }
          </p>
          {selectedDays.length>0&&(
            <div style={{marginTop:"0.8rem",paddingTop:"0.8rem",borderTop:`1px solid ${T.border}`}}>
              <span style={{...css.lbl,marginBottom:"0.55rem"}}>Personen per dag</span>
              {getNext7Days().filter(d=>selectedDays.includes(d.name)).map(d=>{
                const n=getDayPersonen(d.name);
                return (
                  <div key={d.name} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.38rem"}}>
                    <span style={{fontSize:"0.82rem",color:T.textSoft,fontWeight:"500"}}>{d.isToday?"Vandaag":d.short} {d.num} {d.month}</span>
                    <div style={{display:"flex",alignItems:"center",gap:"0.45rem"}}>
                      <button onClick={()=>setDayPersonen(p=>({...p,[d.name]:Math.max(1,n-1)}))} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"8px",width:"28px",height:"28px",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:T.text,fontSize:"1.1rem",fontFamily:F,lineHeight:1}}>−</button>
                      <span style={{fontSize:"0.88rem",fontWeight:"700",color:T.text,minWidth:"18px",textAlign:"center"}}>{n}</span>
                      <button onClick={()=>setDayPersonen(p=>({...p,[d.name]:Math.min(20,n+1)}))} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"8px",width:"28px",height:"28px",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:T.text,fontSize:"1.1rem",fontFamily:F,lineHeight:1}}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button style={css.bigBtn(selectedDays.length===0||loading)} disabled={selectedDays.length===0||loading} onClick={generateMenu}>
          {loading
            ? <><Loader2 size={18} strokeWidth={sw} className="spin"/>{loadingMsg}</>
            : <><Sparkles size={18} strokeWidth={sw}/>Genereer weekmenu</>
          }
        </button>
        {error&&(
          <div style={{background:`${T.accentRed}12`,border:`1px solid ${T.accentRed}30`,borderRadius:"12px",padding:"0.75rem 1rem",marginTop:"0.75rem",fontSize:"0.8rem",color:T.accentRed}}>
            {error}
          </div>
        )}
        <div style={{marginTop:"1.5rem"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.5rem"}}>
            <span style={{...css.lbl,marginBottom:0,display:"flex",alignItems:"center",gap:"0.35rem"}}><Calendar size={11} strokeWidth={sw}/>Recente weken</span>
            {savedWeeks.length===0&&(
              <span style={{fontSize:"0.62rem",color:T.textDim,background:T.surface,border:`1px solid ${T.border}`,borderRadius:"100px",padding:"0.15rem 0.5rem",fontWeight:"500"}}>voorbeeld</span>
            )}
          </div>
          {savedWeeks.length>0?savedWeeks.slice().reverse().slice(0,3).map(w=>(
            <div key={`${w.jaar}-${w.weekNr}`} onClick={()=>loadWeek(w)} style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.5rem",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <span style={{color:T.accent,fontSize:"0.88rem",fontWeight:"700",letterSpacing:"-0.01em"}}>Week {w.weekNr}</span>
                <span style={{color:T.textDim,fontSize:"0.72rem",marginLeft:"0.5rem"}}>{w.datum}</span>
                <div style={{fontSize:"0.73rem",color:T.textDim,marginTop:"0.1rem"}}>{Object.values(w.menu||{}).slice(0,2).map(m=>m.naam).join(", ")}…</div>
              </div>
              <ChevronRight size={18} strokeWidth={sw} color={T.textDim}/>
            </div>
          )):[
            {weekNr:17,datum:"25 apr. 2025",maaltijden:["Pasta Carbonara","Thaise groene curry"]},
            {weekNr:16,datum:"18 apr. 2025",maaltijden:["Zalmfilet met groenten","Mexicaanse wraps"]},
          ].map((w,i)=>(
            <div key={i} style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.5rem",opacity:0.5,pointerEvents:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <span style={{color:T.accent,fontSize:"0.88rem",fontWeight:"700"}}>Week {w.weekNr}</span>
                <span style={{color:T.textDim,fontSize:"0.72rem",marginLeft:"0.5rem"}}>{w.datum}</span>
                <div style={{fontSize:"0.73rem",color:T.textDim,marginTop:"0.1rem"}}>{w.maaltijden.join(", ")}…</div>
              </div>
              <ChevronRight size={18} strokeWidth={sw} color={T.textDim}/>
            </div>
          ))}
          {savedWeeks.length===0&&(
            <div style={{fontSize:"0.74rem",color:T.textDim,textAlign:"center",padding:"0.3rem 0 0.2rem"}}>Sla een gegenereerd menu op om het hier terug te zien</div>
          )}
        </div>
      </div>
      <BottomNav/>
    </div>
  );

  // ── PREFS ─────────────────────────────────────────────────────────────────
  if(screen==="prefs") return (
    <div style={css.wrap}>
      {sideOpen&&<SideMenu/>}
      <Header title="Voorkeuren"/>
      <div style={{flex:1,overflowY:"auto",padding:"1.2rem"}}>
        {[
          ["Kooktijd","tempo",Clock,[["snel",[Zap,"Snel"]],["mix",[Scale,"Mix"]],["culinair",[ChefHat,"Culinair"]]]],
          ["Budget p.p.","budget",Wallet,[["goedkoop",[Coins,"≤€5 p.p."]],["normaal",[Wallet,"€5–10 p.p."]],["luxe",[Gem,"Luxe p.p."]]]],
          ["Focus","focus",Target,[["lekker",[Heart,"Lekker"]],["mix",[Scale,"Balans"]],["gezond",[Leaf,"Gezond"]]]],
          ["E-nummers","enummers",FlaskConical,[["false",[CircleCheck,"Maakt niet uit"]],["true",[CircleX,"Geen E-nummers"]]]],
        ].map(([label,key,LabelIcon,opts])=>(
          <div key={key} style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
            <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><LabelIcon size={11} strokeWidth={sw}/>{label}</span>
            <div>
              {opts.map(([v,[OptIcon,l]])=>(
                <button key={v} style={css.chip(String(prefs[key])===v)} onClick={()=>setPref(key,key==="enummers"?v==="true":v)}>
                  <OptIcon size={13} strokeWidth={sw}/>{l}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Globe size={11} strokeWidth={sw}/>Keukens</span>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem"}}>
            {CUISINES.map(k=>(
              <button key={k} style={css.chip((account.keukens||[]).includes(k))} onClick={()=>setAccount(a=>({...a,keukens:(a.keukens||[]).includes(k)?(a.keukens||[]).filter(x=>x!==k):[...(a.keukens||[]),k]}))}>
                {k}
              </button>
            ))}
          </div>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Zap size={11} strokeWidth={sw}/>Keukenapparatuur</span>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem"}}>
            {EQUIPMENT.map(e=>(
              <button key={e} style={css.chip((account.equipment||[]).includes(e))} onClick={()=>setAccount(a=>({...a,equipment:(a.equipment||[]).includes(e)?(a.equipment||[]).filter(x=>x!==e):[...(a.equipment||[]),e]}))}>
                {e}
              </button>
            ))}
          </div>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><CircleX size={11} strokeWidth={sw}/>Allergieën</span>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem",marginBottom:"0.75rem"}}>
            {COMMON_ALLERGIES.map(a=>(
              <button key={a} style={css.chip((account.allergieenList||[]).includes(a))} onClick={()=>setAccount(p=>({...p,allergieenList:(p.allergieenList||[]).includes(a)?(p.allergieenList||[]).filter(x=>x!==a):[...(p.allergieenList||[]),a]}))}>
                {a}
              </button>
            ))}
          </div>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><PenLine size={11} strokeWidth={sw}/>Overige allergieën</span>
          <input style={css.inp} placeholder="bijv. kiwi, mango…" value={account.allergieen==="kokos"?"":account.allergieen||""} onChange={e=>setAccount(p=>({...p,allergieen:e.target.value}))}/>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><ThumbsDown size={11} strokeWidth={sw}/>Niet lekker</span>
          <textarea style={{...css.inp,resize:"none",minHeight:"64px",lineHeight:"1.6"}} placeholder="bijv. lever, witlof, sterke kazen…" value={account.nietsLekker||""} onChange={e=>setAccount(p=>({...p,nietsLekker:e.target.value}))}/>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Heart size={11} strokeWidth={sw}/>Wat vind je belangrijk bij eten?</span>
          <textarea style={{...css.inp,resize:"none",minHeight:"64px",lineHeight:"1.6"}} placeholder="bijv. duurzaam, seizoensgebonden, weinig vlees…" value={account.belangrijk||""} onChange={e=>setAccount(p=>({...p,belangrijk:e.target.value}))}/>
        </div>
        <button onClick={()=>processToMemory(account.nietsLekker, account.allergieen!=="kokos"?account.allergieen:"", account.belangrijk, prefs.extra)} disabled={memProcessLoading||(!account.nietsLekker&&!account.belangrijk&&!prefs.extra&&(!account.allergieen||account.allergieen==="kokos"))} style={{...css.bigBtn(memProcessLoading||(!account.nietsLekker&&!account.belangrijk&&!prefs.extra&&(!account.allergieen||account.allergieen==="kokos"))),marginBottom:"0.75rem",padding:"0.75rem"}}>
          {memProcessLoading
            ? <><Loader2 size={16} strokeWidth={sw} className="spin"/>Verwerken naar geheugen…</>
            : <><Brain size={16} strokeWidth={sw}/>Verwerk naar smaakgeheugen</>
          }
        </button>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><PenLine size={11} strokeWidth={sw}/>Extra wensen</span>
          <input style={css.inp} placeholder="bijv. geen ui, pasta max 1x…" value={prefs.extra} onChange={e=>setPref("extra",e.target.value)}/>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Brain size={11} strokeWidth={sw}/>Smaakgeheugen</span>
          {memory.liked.length>0&&(
            <div style={{marginBottom:"0.6rem"}}>
              <div style={{fontSize:"0.72rem",color:T.textSoft,marginBottom:"0.3rem",fontWeight:"500",display:"flex",alignItems:"center",gap:"0.3rem"}}><ThumbsUp size={12} strokeWidth={sw} color={T.accentGreen}/>Lekker gevonden</div>
              <div>{memory.liked.map(g=><span key={g} style={{display:"inline-block",background:`${T.accentGreen}15`,border:`1px solid ${T.accentGreen}30`,borderRadius:"100px",padding:"0.22rem 0.65rem",fontSize:"0.76rem",color:T.accentGreen,margin:"0.13rem",fontWeight:"500"}}>{g}</span>)}</div>
            </div>
          )}
          {memory.disliked.length>0&&(
            <div style={{marginBottom:"0.8rem"}}>
              <div style={{fontSize:"0.72rem",color:T.textSoft,marginBottom:"0.3rem",fontWeight:"500",display:"flex",alignItems:"center",gap:"0.3rem"}}><ThumbsDown size={12} strokeWidth={sw} color={T.accentRed}/>Niet lekker</div>
              <div>{memory.disliked.map(g=><span key={g} style={{display:"inline-block",background:`${T.accentRed}12`,border:`1px solid ${T.accentRed}25`,borderRadius:"100px",padding:"0.22rem 0.65rem",fontSize:"0.76rem",color:T.accentRed,margin:"0.13rem",fontWeight:"500"}}>{g}</span>)}</div>
            </div>
          )}
          {memory.liked.length===0&&memory.disliked.length===0&&(
            <p style={{color:T.textDim,fontSize:"0.8rem",marginBottom:"0.8rem"}}>Nog niets onthouden.</p>
          )}
          <button onClick={()=>setMemory({liked:[],disliked:[],notes:[]})} style={{background:`${T.accentRed}10`,border:`1px solid ${T.accentRed}20`,borderRadius:"10px",padding:"0.45rem 0.9rem",color:T.accentRed,cursor:"pointer",fontSize:"0.8rem",fontFamily:F,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.4rem"}}>
            <Trash2 size={14} strokeWidth={sw}/>Geheugen wissen
          </button>
        </div>
      </div>
      <BottomNav/>
    </div>
  );

  // ── ACCOUNT ───────────────────────────────────────────────────────────────
  if(screen==="account") return (
    <div style={css.wrap}>
      {sideOpen&&<SideMenu/>}
      <Header title="Account"/>
      <div style={{flex:1,overflowY:"auto",padding:"1.2rem"}}>
        <div style={{textAlign:"center",marginBottom:"1.5rem"}}>
          <div style={{width:"72px",height:"72px",borderRadius:"50%",overflow:"hidden",margin:"0 auto 0.6rem",boxShadow:`0 8px 24px ${T.accent}30`}}>
            {user?.picture
              ? <img src={user.picture} alt={user.name||""} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : <div style={{width:"100%",height:"100%",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {account.naam
                    ? <span style={{fontSize:"1.5rem",fontWeight:"800",color:"#fff"}}>{account.naam[0].toUpperCase()}</span>
                    : <User size={28} strokeWidth={sw} color="#fff"/>
                  }
                </div>
            }
          </div>
          <div style={{fontSize:"1rem",fontWeight:"700",color:T.text,letterSpacing:"-0.01em"}}>{account.naam||"Naam instellen"}</div>
          <div style={{fontSize:"0.78rem",color:T.textSoft,marginTop:"0.1rem"}}>{account.email||"E-mail instellen"}</div>
          {user?.sub&&(
            <button onClick={handleSignOut} style={{marginTop:"0.75rem",background:`${T.accentRed}10`,border:`1px solid ${T.accentRed}20`,borderRadius:"10px",padding:"0.42rem 1rem",color:T.accentRed,cursor:"pointer",fontSize:"0.78rem",fontFamily:F,fontWeight:"500",display:"inline-flex",alignItems:"center",gap:"0.4rem"}}>
              <LogOut size={13} strokeWidth={sw}/>Uitloggen
            </button>
          )}
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem",marginBottom:"0.75rem"}}>
          {[["naam",User,"text","Je naam…"],["email",Mail,"email","je@email.nl"]].map(([key,Icon,type,ph])=>(
            <div key={key} style={{marginBottom:"0.75rem"}}>
              <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Icon size={11} strokeWidth={sw}/>{key.charAt(0).toUpperCase()+key.slice(1)}</span>
              <input style={css.inp} type={type} placeholder={ph} value={account[key]} onChange={e=>setAccount(p=>({...p,[key]:e.target.value}))}/>
            </div>
          ))}
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Users size={11} strokeWidth={sw}/>Aantal personen</span>
          <div style={{display:"flex",gap:"0.35rem",marginBottom:"0.75rem"}}>
            {[1,2,3,4,5,6].map(n=>(
              <button key={n} style={{...css.chip(account.personen===n),minWidth:"36px",justifyContent:"center",padding:"0.38rem 0",margin:0}} onClick={()=>setAccount(p=>({...p,personen:n}))}>{n}</button>
            ))}
          </div>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><CircleX size={11} strokeWidth={sw}/>Allergieën</span>
          <input style={css.inp} placeholder="bijv. noten, lactose…" value={account.allergieen} onChange={e=>setAccount(p=>({...p,allergieen:e.target.value}))}/>
        </div>
        <div style={{...css.card(false),padding:"0.9rem 1rem"}}>
          <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><BarChart3 size={11} strokeWidth={sw}/>Statistieken</span>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.6rem"}}>
            {[
              [Bookmark,"Weken opgeslagen",savedWeeks.length],
              [Brain,"Geheugenitems",memory.liked.length+memory.disliked.length],
              [ThumbsUp,"Favorieten",memory.liked.length],
              [ThumbsDown,"Vermeden",memory.disliked.length],
            ].map(([Icon,lb,val])=>(
              <div key={lb} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"14px",padding:"0.75rem 0.9rem"}}>
                <Icon size={16} strokeWidth={sw} color={T.accent} style={{marginBottom:"0.3rem"}}/>
                <div style={{fontSize:"1.1rem",fontWeight:"800",color:T.accent,letterSpacing:"-0.02em"}}>{val}</div>
                <div style={{fontSize:"0.68rem",color:T.textDim,marginTop:"0.06rem",fontWeight:"500"}}>{lb}</div>
              </div>
            ))}
          </div>
        </div>
        {(prefs.favorites||[]).length>0&&(
          <div style={{...css.card(false),padding:"0.9rem 1rem",marginTop:"0.75rem"}}>
            <span style={{...css.lbl,display:"flex",alignItems:"center",gap:"0.35rem"}}><Star size={11} strokeWidth={sw}/>Favoriete recepten</span>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.35rem",marginTop:"0.5rem"}}>
              {(prefs.favorites||[]).map(naam=>(
                <div key={naam} style={{display:"inline-flex",alignItems:"center",gap:"0.3rem",background:T.surface,border:`1px solid ${T.border}`,borderRadius:"100px",padding:"0.22rem 0.5rem 0.22rem 0.7rem",fontSize:"0.77rem",color:T.textSoft,fontWeight:"500"}}>
                  <Star size={10} strokeWidth={sw} color="#f59e0b" fill="#f59e0b" style={{flexShrink:0}}/>
                  {naam}
                  <button onClick={()=>setPref("favorites",(prefs.favorites||[]).filter(f=>f!==naam))} style={{background:"none",border:"none",padding:"0.1rem",cursor:"pointer",display:"flex",alignItems:"center",color:T.textDim,marginLeft:"0.1rem"}}>
                    <X size={11} strokeWidth={sw+0.5}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <button onClick={()=>{setShowShareModal(true);setShareUrlCopied(false);}} style={{width:"100%",padding:"0.85rem",borderRadius:"14px",border:`1.5px solid ${T.accent}40`,background:`${T.accent}10`,color:T.accent,fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.5rem",marginTop:"0.75rem",transition:"all 0.15s"}}>
          <Users size={16} strokeWidth={sw}/>Gezinsprofiel delen
        </button>
        <div style={{display:"flex",gap:"0.5rem",marginTop:"0.5rem"}}>
          <button onClick={()=>setShowResetConfirm(true)} style={{flex:1,padding:"0.75rem",borderRadius:"14px",border:`1px solid ${T.border}`,background:"none",color:T.textSoft,fontSize:"0.82rem",fontWeight:"500",cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.4rem",transition:"all 0.15s"}}>
            <RotateCcw size={14} strokeWidth={sw}/>Opnieuw starten
          </button>
          <button onClick={()=>setShowDeleteConfirm(true)} style={{flex:1,padding:"0.75rem",borderRadius:"14px",border:`1px solid ${T.accentRed}30`,background:`${T.accentRed}08`,color:T.accentRed,fontSize:"0.82rem",fontWeight:"500",cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.4rem",transition:"all 0.15s"}}>
            <Trash2 size={14} strokeWidth={sw}/>Account verwijderen
          </button>
        </div>
      </div>
      {showShareModal&&(()=>{const url=generateShareUrl();return(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",backdropFilter:"blur(4px)"}} onClick={()=>setShowShareModal(false)}>
          <div style={{background:T.card,borderRadius:"20px",padding:"1.5rem",width:"100%",maxWidth:"340px",border:`1px solid ${T.border}`,boxShadow:T.shadow}} onClick={e=>e.stopPropagation()}>
            <div style={{width:"48px",height:"48px",borderRadius:"50%",background:`${T.accent}15`,border:`1px solid ${T.accent}30`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 1rem"}}>
              <Users size={22} strokeWidth={sw} color={T.accent}/>
            </div>
            <h3 style={{fontSize:"1.05rem",fontWeight:"700",color:T.text,textAlign:"center",margin:"0 0 0.4rem",letterSpacing:"-0.01em"}}>Gezinsprofiel delen</h3>
            <p style={{color:T.textSoft,fontSize:"0.8rem",textAlign:"center",lineHeight:"1.5",margin:"0 0 1rem"}}>Stuur deze link naar je partner. Zij opent de link en importeert jullie gezinsprofiel direct.</p>
            <div style={{background:T.surface,border:`1px solid ${T.accent}30`,borderRadius:"12px",padding:"0.65rem 0.9rem",marginBottom:"1rem",display:"flex",alignItems:"center",gap:"0.1rem",flexWrap:"wrap"}}>
              <span style={{fontSize:"0.74rem",color:T.textDim,fontWeight:"400"}}>{window.location.host}/</span>
              <span style={{fontSize:"0.78rem",color:T.accent,fontWeight:"700"}}>?join=</span>
              <span style={{fontSize:"0.78rem",color:T.text,fontWeight:"700",letterSpacing:"0.02em"}}>{familyId?.slice(0,8)}<span style={{color:T.textDim}}>…</span></span>
            </div>
            <div style={{display:"flex",gap:"0.5rem"}}>
              <button onClick={()=>setShowShareModal(false)} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:`1px solid ${T.border}`,background:T.surface,color:T.text,fontSize:"0.88rem",fontWeight:"500",cursor:"pointer",fontFamily:F}}>Sluiten</button>
              <button onClick={()=>{navigator.clipboard?.writeText(url).catch(()=>{});setShareUrlCopied(true);}} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:"none",background:shareUrlCopied?T.accentGreen:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,color:"#fff",fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:"0.4rem",transition:"background 0.2s"}}>
                {shareUrlCopied?<><Check size={15} strokeWidth={sw}/>Gekopieerd!</>:<><CornerUpLeft size={15} strokeWidth={sw}/>Kopieer link</>}
              </button>
            </div>
          </div>
        </div>
      );})()}
      {pendingJoin&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",backdropFilter:"blur(4px)"}}>
          <div style={{background:T.card,borderRadius:"20px",padding:"1.5rem",width:"100%",maxWidth:"340px",border:`1px solid ${T.border}`,boxShadow:T.shadow}}>
            <div style={{width:"48px",height:"48px",borderRadius:"50%",background:`${T.accent}15`,border:`1px solid ${T.accent}30`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 1rem"}}>
              <Users size={22} strokeWidth={sw} color={T.accent}/>
            </div>
            <h3 style={{fontSize:"1.05rem",fontWeight:"700",color:T.text,textAlign:"center",margin:"0 0 0.4rem",letterSpacing:"-0.01em"}}>Gezinsprofiel importeren</h3>
            <p style={{color:T.textSoft,fontSize:"0.8rem",textAlign:"center",lineHeight:"1.5",margin:"0 0 0.5rem"}}>Je bent uitgenodigd om deel te nemen aan een gezinsprofiel.</p>
            <p style={{color:T.textDim,fontSize:"0.76rem",textAlign:"center",lineHeight:"1.4",margin:"0 0 1.2rem"}}>Je huidige instellingen worden overschreven. Je smaakgeheugen blijft bewaard.</p>
            <div style={{display:"flex",gap:"0.5rem"}}>
              <button onClick={()=>setPendingJoin(null)} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:`1px solid ${T.border}`,background:T.surface,color:T.text,fontSize:"0.88rem",fontWeight:"500",cursor:"pointer",fontFamily:F}}>Annuleren</button>
              <button onClick={()=>applyJoin(pendingJoin)} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:"none",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,color:"#fff",fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",fontFamily:F}}>Importeren</button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",backdropFilter:"blur(4px)"}} onClick={()=>setShowDeleteConfirm(false)}>
          <div style={{background:T.card,borderRadius:"20px",padding:"1.5rem",width:"100%",maxWidth:"340px",border:`1px solid ${T.border}`,boxShadow:T.shadow}} onClick={e=>e.stopPropagation()}>
            <div style={{width:"48px",height:"48px",borderRadius:"50%",background:`${T.accentRed}15`,border:`1px solid ${T.accentRed}25`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 1rem"}}>
              <Trash2 size={22} strokeWidth={sw} color={T.accentRed}/>
            </div>
            <h3 style={{fontSize:"1.05rem",fontWeight:"700",color:T.text,textAlign:"center",margin:"0 0 0.5rem",letterSpacing:"-0.01em"}}>Account verwijderen?</h3>
            <p style={{color:T.textSoft,fontSize:"0.82rem",textAlign:"center",lineHeight:"1.55",margin:"0 0 1.4rem"}}>Al je gegevens, menu's, smaakgeheugen en inlogstatus worden <strong style={{color:T.accentRed}}>permanent verwijderd</strong>. Je kunt daarna opnieuw beginnen.</p>
            <div style={{display:"flex",gap:"0.5rem"}}>
              <button onClick={()=>setShowDeleteConfirm(false)} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:`1px solid ${T.border}`,background:T.surface,color:T.text,fontSize:"0.88rem",fontWeight:"500",cursor:"pointer",fontFamily:F}}>Annuleren</button>
              <button onClick={deleteAccount} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:"none",background:T.accentRed,color:"#fff",fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",fontFamily:F}}>Ja, verwijderen</button>
            </div>
          </div>
        </div>
      )}
      {showResetConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem",backdropFilter:"blur(4px)"}} onClick={()=>setShowResetConfirm(false)}>
          <div style={{background:T.card,borderRadius:"20px",padding:"1.5rem",width:"100%",maxWidth:"340px",border:`1px solid ${T.border}`,boxShadow:T.shadow}} onClick={e=>e.stopPropagation()}>
            <div style={{width:"48px",height:"48px",borderRadius:"50%",background:`${T.accentRed}15`,border:`1px solid ${T.accentRed}25`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 1rem"}}>
              <AlertTriangle size={22} strokeWidth={sw} color={T.accentRed}/>
            </div>
            <h3 style={{fontSize:"1.05rem",fontWeight:"700",color:T.text,textAlign:"center",margin:"0 0 0.5rem",letterSpacing:"-0.01em"}}>Onboarding opnieuw starten?</h3>
            <p style={{color:T.textSoft,fontSize:"0.82rem",textAlign:"center",lineHeight:"1.55",margin:"0 0 1.4rem"}}>Je <strong style={{color:T.text}}>smaakgeschiedenis</strong> (lekker / niet lekker) wordt gewist. Je kunt daarna alles opnieuw instellen.</p>
            <div style={{display:"flex",gap:"0.5rem"}}>
              <button onClick={()=>setShowResetConfirm(false)} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:`1px solid ${T.border}`,background:T.surface,color:T.text,fontSize:"0.88rem",fontWeight:"500",cursor:"pointer",fontFamily:F}}>Annuleren</button>
              <button onClick={resetOnboarding} style={{flex:1,padding:"0.75rem",borderRadius:"12px",border:"none",background:T.accentRed,color:"#fff",fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",fontFamily:F}}>Ja, opnieuw</button>
            </div>
          </div>
        </div>
      )}
      <BottomNav/>
    </div>
  );

  // ── RESULT ────────────────────────────────────────────────────────────────
  return (
    <div style={css.wrap}>
      {sideOpen&&<SideMenu/>}
      <RecipeModal/>
      <Header
        title="Weekmenu"
        sub={`Week ${weekNr} · ${personen} personen`}
        right={
          <div style={{display:"flex",gap:"0.3rem"}}>
            {Object.keys(menu).length>0&&<>
              <button onClick={printPlanner} style={css.iconBtn(false)} title="Afdrukken">
                <Printer size={16} strokeWidth={sw}/>
              </button>
              <button onClick={saveWeek} style={{background:isSaved?`${T.accentGreen}15`:`${T.accent}15`,border:"none",borderRadius:"10px",padding:"0.38rem 0.65rem",color:isSaved?T.accentGreen:T.accent,cursor:"pointer",fontSize:"0.74rem",fontWeight:"700",fontFamily:F,display:"inline-flex",alignItems:"center",gap:"0.35rem"}}>
                {isSaved?<><CheckCheck size={14} strokeWidth={sw}/>Opgeslagen</>:<><Bookmark size={14} strokeWidth={sw}/>Opslaan</>}
              </button>
            </>}
          </div>
        }
      />

      {Object.keys(budgets).length>0&&!loading&&(
        <div style={{padding:"0.55rem 1.2rem",background:`${T.accentGreen}08`,borderBottom:`1px solid ${T.accentGreen}20`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:"0.72rem",color:T.textSoft,fontWeight:"500",display:"flex",alignItems:"center",gap:"0.35rem"}}>
            <Wallet size={13} strokeWidth={sw}/>Geschat weekbudget
          </span>
          <span style={{fontSize:"0.92rem",fontWeight:"800",color:T.accentGreen,letterSpacing:"-0.02em"}}>€{totalBudget.toFixed(2)}</span>
        </div>
      )}

      {!chatTipSeen&&!loading&&Object.keys(menu).length>0&&(
        <div style={{margin:"0.5rem 1.2rem 0",background:`${T.accent}10`,border:`1px solid ${T.accent}20`,borderRadius:"12px",padding:"0.55rem 0.8rem",display:"flex",alignItems:"center",gap:"0.6rem",flexShrink:0}}>
          <MessageCircle size={13} strokeWidth={sw} color={T.accent} style={{flexShrink:0}}/>
          <span style={{flex:1,fontSize:"0.75rem",color:T.textSoft,lineHeight:"1.35"}}>Tip: gebruik de chat om je menu of voorkeuren aan te passen</span>
          <button onClick={()=>setChatTipSeen(true)} style={{background:"none",border:"none",padding:"0.1rem",cursor:"pointer",color:T.textDim,display:"flex",alignItems:"center",flexShrink:0}}>
            <X size={13} strokeWidth={sw}/>
          </button>
        </div>
      )}

      <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,flexShrink:0,marginTop:"0.5rem"}}>
        <button style={css.tab(view==="menu")} onClick={()=>setView("menu")}>
          <UtensilsCrossed size={13} strokeWidth={sw}/>Menu
        </button>
        <button style={css.tab(view==="lijst")} onClick={()=>setView("lijst")}>
          <ShoppingCart size={13} strokeWidth={sw}/>Lijst{totalItems>0?` (${checkedCount}/${totalItems})`:""}
        </button>
      </div>

      {loading&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"3rem"}}>
          <div style={{width:"52px",height:"52px",borderRadius:"50%",background:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,marginBottom:"1rem",boxShadow:`0 8px 24px ${T.accent}40`,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Loader2 size={24} strokeWidth={sw} color="#fff" className="spin"/>
          </div>
          <p style={{color:T.accent,fontSize:"0.9rem",fontWeight:"600",margin:0}}>{loadingMsg}</p>
          <p style={{color:T.textDim,fontSize:"0.78rem",marginTop:"0.3rem"}}>Even geduld…</p>
        </div>
      )}

      {!loading&&view==="menu"&&Object.keys(menu).length===0&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2.5rem 1.5rem",textAlign:"center"}}>
          <div style={{width:"64px",height:"64px",borderRadius:"50%",background:T.surface,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"1.1rem"}}>
            <UtensilsCrossed size={26} strokeWidth={sw} color={T.textDim}/>
          </div>
          <div style={{fontSize:"1rem",fontWeight:"700",color:T.text,marginBottom:"0.4rem",letterSpacing:"-0.01em"}}>Nog geen menu gecreëerd</div>
          <div style={{fontSize:"0.83rem",color:T.textSoft,lineHeight:"1.5",marginBottom:"1.4rem"}}>Ga naar home om een weekmenu te laten genereren</div>
          <button onClick={()=>setScreen("home")} style={{...css.bigBtn(false),width:"auto",padding:"0.75rem 1.5rem"}}>
            <Sparkles size={15} strokeWidth={sw}/>Maak een menu
          </button>
        </div>
      )}

      {!loading&&view==="menu"&&Object.keys(menu).length>0&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <SwipeView/>
        </div>
      )}

      {!loading&&view==="lijst"&&(
        <div style={{flex:1,overflowY:"auto",padding:"1rem 1.2rem 2rem"}}>
          {totalItems===0&&(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"3rem 1rem",textAlign:"center"}}>
              <div style={{width:"64px",height:"64px",borderRadius:"50%",background:T.surface,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"1.1rem"}}>
                <ShoppingCart size={26} strokeWidth={sw} color={T.textDim}/>
              </div>
              <div style={{fontSize:"1rem",fontWeight:"700",color:T.text,marginBottom:"0.4rem",letterSpacing:"-0.01em"}}>Geen boodschappenlijst</div>
              <div style={{fontSize:"0.83rem",color:T.textSoft,lineHeight:"1.5",marginBottom:"1.4rem"}}>Genereer eerst een weekmenu om een boodschappenlijst te krijgen</div>
              <button onClick={()=>setScreen("home")} style={{...css.bigBtn(false),width:"auto",padding:"0.75rem 1.5rem"}}>
                <Sparkles size={15} strokeWidth={sw}/>Maak een menu
              </button>
            </div>
          )}
          {totalItems>0&&(
            <div style={{...css.card(false),padding:"0.85rem 1rem",marginBottom:"1rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.5rem"}}>
                <span style={{fontSize:"0.72rem",color:T.textSoft,fontWeight:"500"}}>Voortgang</span>
                <span style={{fontSize:"0.72rem",color:T.accent,fontWeight:"700"}}>{checkedCount}/{totalItems}</span>
              </div>
              <div style={{background:T.border,borderRadius:"100px",height:"5px",overflow:"hidden"}}>
                <div style={{background:`linear-gradient(90deg,${T.accent},${T.accentWarm})`,height:"100%",width:`${(checkedCount/totalItems)*100}%`,borderRadius:"100px",transition:"width 0.3s"}}/>
              </div>
            </div>
          )}
          {Array.isArray(boodschappen)&&boodschappen.map(cat=>(
            <div key={cat.categorie} style={{marginBottom:"1rem"}}>
              <div style={{...css.lbl,marginBottom:"0.4rem"}}>{cat.categorie}</div>
              <div style={{background:T.card,borderRadius:"14px",border:`1px solid ${T.border}`,overflow:"hidden",boxShadow:T.shadow}}>
                {cat.items.map((item,idx)=>{
                  const k=`${cat.categorie}::${item}`;const done=checked[k];
                  return(
                    <div key={item} onClick={()=>toggleCheck(cat.categorie,item)} style={{display:"flex",alignItems:"center",gap:"0.7rem",padding:"0.65rem 0.9rem",borderBottom:idx<cat.items.length-1?`1px solid ${T.border}`:"none",cursor:"pointer",background:done?`${T.accent}06`:"transparent",transition:"background 0.15s"}}>
                      <div style={{width:"18px",height:"18px",borderRadius:"6px",flexShrink:0,border:done?"none":`1.5px solid ${T.border}`,background:done?`linear-gradient(135deg,${T.accent},${T.accentWarm})`:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {done&&<Check size={11} strokeWidth={3} color="#fff"/>}
                      </div>
                      <span style={{fontSize:"0.84rem",color:done?T.textDim:T.text,textDecoration:done?"line-through":"none",textTransform:"capitalize"}}>{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading&&view==="menu"&&Object.keys(menu).length>0&&(
        <div style={{borderTop:`1px solid ${T.border}`,background:T.navBg,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",flexShrink:0}}>
          {chatOpen&&(
            <div style={{maxHeight:"220px",overflowY:"auto",padding:"0.75rem 1.2rem 0.5rem",borderBottom:`1px solid ${T.border}`}}>
              {chatHistory.length===0&&(
                <div style={{paddingBottom:"0.35rem"}}>
                  <div style={{...css.lbl,marginBottom:"0.4rem"}}>Voorbeelden</div>
                  <div style={{display:"flex",gap:"0.3rem",flexWrap:"wrap"}}>
                    {["Vervang maandag door iets met vis","Maak het menu vegetarisch","Iets snellers op donderdag","Meer Aziatisch","Goedkopere opties","Ik vond de pasta heerlijk","Minder vlees","Wissel dinsdag en vrijdag","Iets feestelijks voor zaterdag"].map(ex=>(
                      <button key={ex} onClick={()=>setChatInput(ex)} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"100px",padding:"0.28rem 0.65rem",color:T.textSoft,cursor:"pointer",fontSize:"0.75rem",fontFamily:F,fontWeight:"500"}}>{ex}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatHistory.map((msg,i)=>(
                <div key={i} style={{display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start",marginBottom:"0.45rem"}}>
                  <div style={{maxWidth:"84%",padding:"0.5rem 0.8rem",borderRadius:msg.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:msg.role==="user"?T.chatUser:T.chatBot,border:`1px solid ${T.border}`,fontSize:"0.82rem",color:msg.role==="user"?T.accent:T.textSoft,lineHeight:"1.5"}}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading&&(
                <div style={{display:"flex",gap:"0.3rem",paddingBottom:"0.3rem",alignItems:"center"}}>
                  {[0,0.15,0.3].map((d,i)=><div key={i} style={{width:"6px",height:"6px",borderRadius:"50%",background:T.textDim,animation:`bl 1.2s ${d}s infinite`}}/>)}
                  <style>{`@keyframes bl{0%,100%{opacity:.2;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}`}</style>
                </div>
              )}
              <div ref={chatEndRef}/>
            </div>
          )}
          <div style={{padding:"0.6rem 1.2rem 0.7rem",display:"flex",gap:"0.4rem",alignItems:"center"}}>
            <button onClick={()=>setChatOpen(p=>!p)} style={{...css.iconBtn(chatOpen),flexShrink:0}}>
              {chatOpen?<X size={16} strokeWidth={sw}/>:<MessageCircle size={16} strokeWidth={sw}/>}
            </button>
            <input ref={chatInputRef} style={{...css.inp,flex:1,padding:"0.5rem 0.85rem"}} placeholder="Pas het menu aan…" value={chatInput} onChange={e=>setChatInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendChat()} onFocus={()=>setChatOpen(true)} disabled={chatLoading}/>
            <button onClick={sendChat} disabled={!chatInput.trim()||chatLoading} style={{background:(!chatInput.trim()||chatLoading)?T.surface:`linear-gradient(135deg,${T.accent},${T.accentWarm})`,border:`1px solid ${(!chatInput.trim()||chatLoading)?T.border:"transparent"}`,borderRadius:"10px",padding:"0.5rem 0.75rem",color:(!chatInput.trim()||chatLoading)?T.textDim:"#fff",cursor:(!chatInput.trim()||chatLoading)?"not-allowed":"pointer",display:"flex",alignItems:"center",transition:"all 0.15s"}}>
              <ArrowUp size={16} strokeWidth={sw}/>
            </button>
          </div>
        </div>
      )}
      <BottomNav/>
    </div>
  );
}
